import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { BrandMark, EventismCredit } from "@/components/Brand";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const SECTIONS = [
  { id: "snapshot", label: "Stock Snapshot", icon: "fa-chart-pie" },
  { id: "rentals", label: "Live Rentals", icon: "fa-calendar-check" },
  { id: "financials", label: "Financials", icon: "fa-dollar-sign" },
  { id: "access-codes", label: "Access Codes", icon: "fa-key" },
  { id: "network", label: "Pod Network", icon: "fa-map-pin" },
  { id: "equipment", label: "Equipment", icon: "fa-box-open" },
  { id: "lock", label: "Lock Activity", icon: "fa-lock" },
  { id: "calendar", label: "Calendar Sync", icon: "fa-calendar-days" },
  { id: "crm", label: "Customers (CRM)", icon: "fa-users" },
  { id: "ids", label: "ID Documents", icon: "fa-id-card" },
];

const MARKETING_SECTIONS = [
  { id: "newsletters", label: "Newsletters", icon: "fa-paper-plane" },
  { id: "broadcasts", label: "Broadcasts", icon: "fa-bullhorn" },
  { id: "campaigns", label: "Digital Campaigns", icon: "fa-rocket" },
  { id: "field", label: "Field Marketing", icon: "fa-map-location-dot" },
  { id: "wishlist", label: "Equipment Wishlist", icon: "fa-lightbulb" },
];

const STATUS_LABEL = {
  pending_payment: { label: "Pending", color: "bg-amber-100 text-amber-700" },
  paid: { label: "Active", color: "bg-emerald-100 text-emerald-700" },
  active: { label: "Active", color: "bg-emerald-100 text-emerald-700" },
  completed: { label: "Done", color: "bg-slate-100 text-slate-600" },
  cancelled: { label: "Cancelled", color: "bg-red-100 text-red-700" },
};

export default function Admin() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [section, setSection] = useState("snapshot");
  const [stock, setStock] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [lockEvents, setLockEvents] = useState([]);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [showCreateRental, setShowCreateRental] = useState(false);

  const refreshBookings = async () => {
    try { const b = await api.get("/admin/bookings"); setBookings(b.data); } catch { /* silent */ }
  };

  useEffect(() => {
    if (!loading && (!user || user.role !== "admin")) navigate("/login");
  }, [user, loading, navigate]);

  const refresh = async () => {
    setBusy(true);
    try {
      const [s, b, le] = await Promise.all([
        api.get("/admin/stock"),
        api.get("/admin/bookings"),
        api.get("/admin/lock-events"),
      ]);
      setStock(s.data);
      setBookings(b.data);
      setLockEvents(le.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Refresh failed");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (user?.role === "admin") refresh();
  }, [user]);

  if (loading || !user || user.role !== "admin") return null;

  const totals = stock.reduce(
    (acc, s) => {
      const key = s.icon === "fa-chair" ? "chairs" : s.icon === "fa-table" || s.icon === "fa-circle" ? "tables" : s.icon === "fa-campground" ? "tents" : "other";
      acc[key] = (acc[key] || 0) + s.stock_total;
      acc.totalIn = (acc.totalIn || 0) + s.in_pod;
      acc.totalRented = (acc.totalRented || 0) + s.rented;
      return acc;
    },
    {},
  );

  const activeRentals = bookings.filter((b) => b.status === "paid" || b.status === "active");

  return (
    <div className="bg-brand-slate min-h-screen flex">
      {/* Sidebar */}
      <aside className="w-64 bg-brand-blue text-white flex flex-col fixed h-full z-50" data-testid="admin-sidebar">
        <div className="p-6 border-b border-white/10">
          <BrandMark light compact />
          <p className="mt-3 text-[10px] font-black uppercase tracking-widest text-brand-gold">
            Command Center
          </p>
          <p className="text-[10px] text-slate-300 font-bold uppercase tracking-widest mt-1">
            Pod 01 • Park Hill
          </p>
        </div>
        <nav className="flex-1 p-4 space-y-1.5 overflow-y-auto">
          {SECTIONS.map((s) => (
            <button
              key={s.id}
              onClick={() => setSection(s.id)}
              className={`w-full flex items-center p-3 rounded-xl font-black uppercase text-[10px] tracking-widest transition-all ${
                section === s.id
                  ? "bg-brand-gold text-brand-blue"
                  : "text-slate-300 hover:bg-white/5"
              }`}
              data-testid={`admin-nav-${s.id}`}
            >
              <i className={`fas ${s.icon} mr-3`} />
              {s.label}
            </button>
          ))}

          <div className="pt-4 mt-2 border-t border-white/10">
            <p className="px-3 pb-2 text-[9px] font-black uppercase tracking-[0.3em] text-brand-gold">
              <i className="fas fa-bullseye mr-1.5" /> Marketing
            </p>
            {MARKETING_SECTIONS.map((s) => (
              <button
                key={s.id}
                onClick={() => setSection(s.id)}
                className={`w-full flex items-center p-3 rounded-xl font-black uppercase text-[10px] tracking-widest transition-all ${
                  section === s.id
                    ? "bg-brand-gold text-brand-blue"
                    : "text-slate-300 hover:bg-white/5"
                }`}
                data-testid={`admin-nav-${s.id}`}
              >
                <i className={`fas ${s.icon} mr-3`} />
                {s.label}
              </button>
            ))}
          </div>
        </nav>
        <div className="p-6 border-t border-white/10">
          <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-3">
            {user.email}
          </div>
          <button
            onClick={() => {
              logout();
              navigate("/");
            }}
            className="w-full bg-brand-red text-white py-3 rounded-xl font-black uppercase text-[10px] tracking-widest hover:bg-red-800 transition-colors"
            data-testid="admin-logout-button"
          >
            <i className="fas fa-sign-out-alt mr-2" /> Log Out
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="ml-64 flex-1 p-8 min-h-screen">
        {/* Stock Snapshot */}
        {section === "snapshot" && (
          <section data-testid="admin-section-snapshot" className="space-y-8">
            <div className="flex items-end justify-between flex-wrap gap-3">
              <div>
                <p className="text-[10px] font-black uppercase tracking-[0.4em] text-brand-red mb-2">
                  Live Inventory
                </p>
                <h1 className="text-4xl font-black uppercase tracking-tightest text-brand-blue">
                  Pod Snapshot
                </h1>
                <p className="text-slate-500 font-bold uppercase text-[10px] tracking-widest mt-1">
                  3475 Holly St • {busy ? "Refreshing..." : "Updated just now"}
                </p>
              </div>
              <button
                onClick={refresh}
                className="bg-white border border-slate-200 px-5 py-2.5 rounded-full text-[10px] font-black uppercase tracking-widest text-brand-blue hover:bg-brand-gold hover:border-brand-gold transition-colors"
                data-testid="admin-refresh-button"
              >
                <i className="fas fa-rotate mr-2" /> Refresh
              </button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <KPI label="Items in Pod" value={totals.totalIn || 0} accent="border-brand-gold" />
              <KPI label="Items Out" value={totals.totalRented || 0} accent="border-brand-red" />
              <KPI label="Active Rentals" value={activeRentals.length} accent="border-brand-blue" />
              <KPI label="Total Bookings" value={bookings.length} accent="border-slate-300" />
            </div>

            <div className="bg-white rounded-[32px] shadow-sm overflow-hidden border border-slate-100">
              <table className="w-full text-left">
                <thead className="bg-slate-50 text-[10px] uppercase font-black tracking-widest text-slate-500">
                  <tr>
                    <th className="p-5">Item</th>
                    <th className="p-5 text-center">In Pod</th>
                    <th className="p-5 text-center">Out</th>
                    <th className="p-5 text-center">Total</th>
                    <th className="p-5 text-right">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {stock.map((s) => (
                    <tr key={s.id} className="border-t border-slate-50 hover:bg-slate-50/50 transition-colors" data-testid={`stock-row-${s.id}`}>
                      <td className="p-5 flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-brand-blue/5 text-brand-blue flex items-center justify-center">
                          <i className={`fas ${s.icon}`} />
                        </div>
                        <div>
                          <p className="text-xs font-black uppercase text-brand-blue">{s.name}</p>
                          <p className="text-[10px] font-bold text-slate-400">${s.price_per_day.toFixed(2)}/day</p>
                        </div>
                      </td>
                      <td className="p-5 text-center font-black text-brand-blue text-lg">{s.in_pod}</td>
                      <td className="p-5 text-center font-bold text-brand-red">{s.rented}</td>
                      <td className="p-5 text-center font-bold text-slate-500">{s.stock_total}</td>
                      <td className="p-5 text-right">
                        <span className={`text-[9px] font-black uppercase tracking-widest px-2 py-1 rounded ${
                          s.health === "good"
                            ? "bg-emerald-100 text-emerald-700"
                            : s.health === "medium"
                              ? "bg-amber-100 text-amber-700"
                              : "bg-red-100 text-red-700"
                        }`}>
                          {s.health}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {/* Live Rentals */}
        {section === "rentals" && (
          <section data-testid="admin-section-rentals" className="space-y-6">
            <div className="flex items-end justify-between gap-3">
              <div>
                <p className="text-[10px] font-black uppercase tracking-[0.4em] text-brand-red mb-2">
                  Active Orders
                </p>
                <h1 className="text-4xl font-black uppercase tracking-tightest text-brand-blue">
                  Live Rentals
                </h1>
              </div>
              <button
                onClick={() => setShowCreateRental(true)}
                className="bg-brand-red text-white px-5 py-3 rounded-xl font-black uppercase text-[10px] tracking-widest hover:opacity-90 transition-opacity"
                data-testid="rentals-create-btn"
              >
                <i className="fas fa-plus mr-1.5" /> Create Rental
              </button>
            </div>

            {showCreateRental && (
              <CreateRentalDialog
                onClose={() => setShowCreateRental(false)}
                onCreated={() => { setShowCreateRental(false); refreshBookings(); }}
              />
            )}

            <div className="bg-white rounded-[32px] shadow-sm overflow-hidden border border-slate-100">
              <table className="w-full text-left text-sm">
                <thead className="bg-slate-50 text-[10px] uppercase font-black tracking-widest text-slate-500">
                  <tr>
                    <th className="p-4">Ref</th>
                    <th className="p-4">Customer</th>
                    <th className="p-4">Source</th>
                    <th className="p-4">Window</th>
                    <th className="p-4">Pod</th>
                    <th className="p-4">PIN</th>
                    <th className="p-4">Codes</th>
                    <th className="p-4 text-right">Total</th>
                    <th className="p-4 text-right">Late Fee</th>
                    <th className="p-4 text-right">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {bookings.map((b) => {
                    const s = STATUS_LABEL[b.status] || STATUS_LABEL.pending_payment;
                    return (
                      <tr key={b.id} className="border-t border-slate-50" data-testid={`admin-rental-${b.id}`}>
                        <td className="p-4 font-black text-brand-blue">#{b.id.slice(0, 8).toUpperCase()}</td>
                        <td className="p-4 font-bold">
                          <p className="text-brand-blue">{b.customer_name || b.user_email || "Anon"}</p>
                          <p className="text-[10px] text-slate-400">{b.user_email}</p>
                        </td>
                        <td className="p-4">
                          <span className={`text-[9px] font-black uppercase tracking-widest px-2 py-1 rounded ${
                            b.source === "eventism" ? "bg-brand-gold/20 text-brand-blue" : "bg-slate-100 text-slate-600"
                          }`}>
                            {b.source}
                          </span>
                        </td>
                        <td className="p-4 text-[10px] font-bold text-slate-500">
                          {new Date(b.start_date).toLocaleDateString()}
                          <br />
                          {b.rental_days}d
                        </td>
                        <td className="p-4">
                          <span className="text-[9px] font-black uppercase tracking-widest bg-brand-blue text-brand-gold px-2 py-1 rounded-full" data-testid={`pod-cell-${b.id}`}>
                            <i className="fas fa-map-pin mr-1" /> {(b.pod_slug || "park-hill").replace(/-/g, " ")}
                          </span>
                        </td>
                        <td className="p-4 font-mono font-black text-brand-red">{b.pin || "—"}</td>
                        <td className="p-4">
                          {b.access_codes_sent_at ? (
                            <span className="text-[9px] font-black uppercase bg-emerald-100 text-emerald-800 border border-emerald-200 px-2 py-1 rounded" data-testid={`ac-status-${b.id}`}>
                              <i className="fas fa-key mr-1" /> Sent
                            </span>
                          ) : (b.status === "paid" || b.status === "active") ? (
                            <button
                              onClick={() => { setSection("access-codes"); }}
                              className="text-[9px] font-black uppercase bg-red-100 text-red-800 border border-red-300 px-2 py-1 rounded hover:bg-red-200 transition-colors"
                              data-testid={`ac-needs-${b.id}`}
                              title="Enter access codes"
                            >
                              <i className="fas fa-triangle-exclamation mr-1" /> Needs Codes
                            </button>
                          ) : (
                            <span className="text-slate-300 text-[10px]">—</span>
                          )}
                        </td>
                        <td className="p-4 text-right font-black text-brand-blue">${b.total.toFixed(2)}</td>
                        <td className="p-4 text-right">
                          {b.late_fee_due > 0 ? (
                            <span className="text-brand-red font-black">${b.late_fee_due.toFixed(2)}</span>
                          ) : (
                            <span className="text-slate-300">—</span>
                          )}
                          {b.status === "completed" && !b.deposit_settled && (
                            <button
                              onClick={async () => {
                                if (!window.confirm(
                                  b.late_fee_due > 0
                                    ? `Capture $${b.deposit_amount.toFixed(2)} deposit + $${b.late_fee_due.toFixed(2)} late fee?`
                                    : `Refund $${b.deposit_amount.toFixed(2)} deposit?`
                                )) return;
                                try {
                                  await api.post(`/admin/bookings/${b.id}/settle`, {
                                    capture_deposit: b.late_fee_due > 0,
                                  });
                                  toast.success("Settled");
                                  refresh();
                                } catch (e) {
                                  toast.error("Settle failed");
                                }
                              }}
                              className="ml-2 text-[9px] font-black uppercase tracking-widest text-brand-blue underline"
                              data-testid={`settle-button-${b.id}`}
                            >
                              Settle
                            </button>
                          )}
                          {b.deposit_settled && (
                            <span className="ml-2 text-[9px] font-black uppercase tracking-widest text-emerald-600">
                              ✓ Settled
                            </span>
                          )}
                        </td>
                        <td className="p-4 text-right">
                          <span className={`px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest ${s.color}`}>
                            {s.label}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {/* Equipment */}
        {section === "equipment" && (
          <section data-testid="admin-section-equipment" className="space-y-6">
            <div className="flex justify-between items-end">
              <div>
                <p className="text-[10px] font-black uppercase tracking-[0.4em] text-brand-red mb-2">
                  Inventory CRUD
                </p>
                <h1 className="text-4xl font-black uppercase tracking-tightest text-brand-blue">
                  Equipment
                </h1>
              </div>
              <button
                onClick={() => setCreating(true)}
                className="btn-primary px-5 py-3 rounded-xl font-black uppercase text-[10px] tracking-widest"
                data-testid="admin-create-item-button"
              >
                <i className="fas fa-plus mr-2" /> New Item
              </button>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {stock.map((s) => (
                <div
                  key={s.id}
                  className="bg-white rounded-[24px] p-5 border border-slate-100 shadow-sm border-b-8 border-b-brand-blue/20"
                  data-testid={`equipment-card-${s.id}`}
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-12 h-12 rounded-xl bg-brand-blue text-brand-gold flex items-center justify-center">
                      <i className={`fas ${s.icon} text-xl`} />
                    </div>
                    <div>
                      <p className="font-black text-xs uppercase text-brand-blue tracking-tightest">
                        {s.name}
                      </p>
                      <p className="text-[10px] font-bold text-slate-500">{s.category}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <Mini label="Price" value={`$${s.price_per_day.toFixed(0)}`} />
                    <Mini label="Stock" value={s.stock_total} />
                    <Mini label="Out" value={s.rented} />
                  </div>
                  <div className="flex gap-2 mt-4">
                    <button
                      onClick={() => setEditing(s)}
                      className="flex-1 bg-slate-100 text-brand-blue py-2 rounded-lg font-black uppercase text-[10px] tracking-widest hover:bg-brand-gold transition-colors"
                      data-testid={`equipment-edit-${s.id}`}
                    >
                      Edit
                    </button>
                    <button
                      onClick={async () => {
                        if (!window.confirm(`Delete ${s.name}?`)) return;
                        try {
                          await api.delete(`/inventory/${s.id}`);
                          toast.success("Deleted");
                          refresh();
                        } catch (e) {
                          toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
                        }
                      }}
                      className="bg-brand-red text-white py-2 px-3 rounded-lg font-black uppercase text-[10px] tracking-widest hover:bg-red-800 transition-colors"
                      data-testid={`equipment-delete-${s.id}`}
                    >
                      <i className="fas fa-trash" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Lock Activity */}
        {section === "lock" && (
          <section data-testid="admin-section-lock" className="space-y-6">
            <div>
              <p className="text-[10px] font-black uppercase tracking-[0.4em] text-brand-red mb-2">
                eSmartLock P12BC • Mock Service
              </p>
              <h1 className="text-4xl font-black uppercase tracking-tightest text-brand-blue">
                Lock Activity
              </h1>
              <p className="text-slate-500 font-bold text-xs mt-2">
                Switch this list to live <code className="bg-slate-100 px-2 py-0.5 rounded">getLockRecord</code>{" "}
                calls once your AppKey/AppSecret are provisioned.
              </p>
            </div>

            <div className="bg-white rounded-[32px] shadow-sm overflow-hidden border border-slate-100">
              <table className="w-full text-left text-sm">
                <thead className="bg-slate-50 text-[10px] uppercase font-black tracking-widest text-slate-500">
                  <tr>
                    <th className="p-4">When</th>
                    <th className="p-4">Type</th>
                    <th className="p-4">Booking</th>
                    <th className="p-4">PIN</th>
                    <th className="p-4">Window</th>
                  </tr>
                </thead>
                <tbody>
                  {lockEvents.map((e) => (
                    <tr key={e.id} className="border-t border-slate-50" data-testid={`lock-event-${e.id}`}>
                      <td className="p-4 font-bold text-slate-600">
                        {new Date(e.created_at).toLocaleString()}
                      </td>
                      <td className="p-4">
                        <span className="text-[9px] font-black uppercase tracking-widest px-2 py-1 rounded bg-brand-blue text-brand-gold">
                          {e.type}
                        </span>
                      </td>
                      <td className="p-4 font-mono text-[10px] text-slate-500">
                        {e.booking_id?.slice(0, 8)}
                      </td>
                      <td className="p-4 font-mono font-black text-brand-red">{e.pin || "—"}</td>
                      <td className="p-4 text-[10px] font-bold text-slate-500">
                        {e.starts_at ? `${new Date(e.starts_at).toLocaleDateString()} → ${new Date(e.ends_at).toLocaleDateString()}` : "—"}
                      </td>
                    </tr>
                  ))}
                  {lockEvents.length === 0 && (
                    <tr>
                      <td colSpan={5} className="p-10 text-center text-slate-400 font-bold">
                        No PIN events yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {/* Calendar Sync */}
        {section === "calendar" && <CalendarSection />}

        {/* CRM — Customer 360 */}
        {section === "crm" && <CRMSection />}

        {/* ID Documents */}
        {section === "ids" && <CustomerIDsSection />}

        {/* Newsletters */}
        {section === "newsletters" && <NewslettersSection />}

        {/* Broadcasts */}
        {section === "broadcasts" && <BroadcastsSection />}

        {/* Equipment Wishlist */}
        {section === "wishlist" && <WishlistSection />}

        {/* Digital Campaigns */}
        {section === "campaigns" && <CampaignsSection />}

        {/* Field Marketing */}
        {section === "field" && <FieldMarketingSection />}

        {/* Access Codes — manual eSmartLock workflow */}
        {section === "access-codes" && <AccessCodesSection />}

        {/* Pod Network — consolidated locations + equipment + activity */}
        {section === "network" && <PodNetworkAdminSection />}
        {section === "financials" && <FinancialsSection />}

        <div className="mt-12 pt-6 border-t border-slate-200 text-center">
          <EventismCredit />
        </div>
      </main>

      <ItemDialog
        item={editing}
        open={!!editing || creating}
        creating={creating}
        onClose={() => {
          setEditing(null);
          setCreating(false);
        }}
        onSaved={refresh}
      />
    </div>
  );
}

function CalendarSection() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api
      .get("/calendar/status")
      .then(({ data }) => setStatus(data))
      .catch(() => setStatus(null));
  }, []);

  // If returning from OAuth, show toast
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("calendar") === "connected") {
      toast.success(`Google Calendar connected: ${params.get("email") || ""}`);
      window.history.replaceState({}, "", "/admin");
    }
  }, []);

  const connect = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/calendar/connect");
      window.location.href = data.authorization_url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Connect failed");
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect Google Calendar?")) return;
    try {
      await api.post("/calendar/disconnect");
      toast.success("Disconnected");
      const { data } = await api.get("/calendar/status");
      setStatus(data);
    } catch (e) {
      toast.error("Disconnect failed");
    }
  };

  const runLifecycle = async () => {
    try {
      const { data } = await api.post("/admin/lifecycle/run");
      toast.success(`Lifecycle tick complete @ ${new Date(data.ran_at).toLocaleTimeString()}`);
    } catch (e) {
      toast.error("Tick failed");
    }
  };

  if (!status) return <p className="text-slate-500 font-bold">Loading...</p>;

  return (
    <section data-testid="admin-section-calendar" className="space-y-6">
      <div>
        <p className="text-[10px] font-black uppercase tracking-[0.4em] text-brand-red mb-2">
          Pod-Owner Sync
        </p>
        <h1 className="text-4xl font-black uppercase tracking-tightest text-brand-blue">
          Calendar Sync
        </h1>
        <p className="text-slate-500 font-bold text-xs mt-2">
          Auto-creates an event on your Google Calendar for every paid booking.
        </p>
      </div>

      {!status.oauth_configured ? (
        <div className="bg-amber-50 border-l-4 border-amber-400 rounded-r-2xl p-5" data-testid="calendar-oauth-missing">
          <p className="font-black text-amber-800 uppercase text-xs tracking-widest mb-2">
            <i className="fas fa-triangle-exclamation mr-2" /> Google OAuth not configured
          </p>
          <p className="text-sm text-amber-900 font-bold leading-relaxed">
            Add{" "}
            <code className="bg-amber-200/40 px-2 py-0.5 rounded">GOOGLE_CLIENT_ID</code>{" "}
            and{" "}
            <code className="bg-amber-200/40 px-2 py-0.5 rounded">GOOGLE_CLIENT_SECRET</code>{" "}
            to <code className="bg-amber-200/40 px-2 py-0.5 rounded">backend/.env</code>, then
            restart the backend. Authorized redirect URI:{" "}
            <code className="bg-amber-200/40 px-2 py-0.5 rounded break-all">
              {window.location.origin}/api/calendar/callback
            </code>
          </p>
        </div>
      ) : status.connected ? (
        <div
          className="bg-emerald-50 border border-emerald-200 rounded-[24px] p-6 flex items-center justify-between flex-wrap gap-4"
          data-testid="calendar-connected"
        >
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-emerald-500 text-white flex items-center justify-center text-xl">
              <i className="fab fa-google" />
            </div>
            <div>
              <p className="text-[10px] font-black uppercase tracking-widest text-emerald-700">
                Connected
              </p>
              <p className="font-black text-brand-blue text-lg">{status.email}</p>
              <p className="text-xs font-bold text-slate-500">
                Calendar: {status.calendar_id}
              </p>
            </div>
          </div>
          <button
            onClick={disconnect}
            className="bg-white border border-slate-200 px-5 py-2.5 rounded-full text-[10px] font-black uppercase tracking-widest text-brand-red hover:bg-brand-red hover:text-white hover:border-brand-red transition-colors"
            data-testid="calendar-disconnect-button"
          >
            Disconnect
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-[24px] p-6 border border-slate-100" data-testid="calendar-not-connected">
          <p className="font-bold text-slate-600 mb-4">
            Authorize Google Calendar so paid bookings sync automatically.
          </p>
          <button
            onClick={connect}
            disabled={busy}
            className="btn-primary px-6 py-3 rounded-xl font-black uppercase text-xs tracking-widest disabled:opacity-50 inline-flex items-center gap-3"
            data-testid="calendar-connect-button"
          >
            <i className="fab fa-google" />
            {busy ? "Redirecting..." : "Connect Google Calendar"}
          </button>
        </div>
      )}

      <div className="bg-white rounded-[24px] p-6 border border-slate-100">
        <p className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-2">
          Manual Cron Tick
        </p>
        <p className="text-xs font-bold text-slate-500 mb-4 leading-relaxed">
          Lifecycle scheduler runs every 5 minutes (paid → active → completed, late fee
          assessment). Run it manually now if needed.
        </p>
        <button
          onClick={runLifecycle}
          className="bg-brand-blue text-white px-5 py-2.5 rounded-full text-[10px] font-black uppercase tracking-widest hover:bg-brand-blue/90 transition-colors"
          data-testid="lifecycle-run-button"
        >
          <i className="fas fa-rotate mr-2" /> Run Lifecycle Tick
        </button>
      </div>
    </section>
  );
}

function KPI({ label, value, accent }) {
  return (
    <div className={`bg-white p-5 rounded-[24px] shadow-sm border-b-4 ${accent}`} data-testid={`kpi-${label.toLowerCase().replace(/ /g, "-")}`}>
      <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">{label}</p>
      <p className="text-3xl font-black text-brand-blue">{value}</p>
    </div>
  );
}

function Mini({ label, value }) {
  return (
    <div className="bg-slate-50 rounded-lg py-2">
      <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">{label}</p>
      <p className="font-black text-sm text-brand-blue">{value}</p>
    </div>
  );
}

function ItemDialog({ item, open, creating, onClose, onSaved }) {
  const empty = {
    name: "",
    price_per_day: 0,
    icon: "fa-box",
    image_url: "",
    description: "",
    category: "Standard",
    stock_total: 1,
    sort_order: 99,
    instant_booking: true,
    extras: [],
  };
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (item) setForm({ ...empty, ...item, extras: item.extras || [] });
    else if (creating) setForm(empty);
  }, [item, creating]);

  const save = async () => {
    setBusy(true);
    try {
      // Validate extras: every extra needs id + label + price_type + numeric price
      const cleanedExtras = (form.extras || []).map((e) => ({
        id: (e.id || "").trim(),
        label: (e.label || "").trim(),
        description: e.description || "",
        price_type: e.price_type || "per_unit_per_day",
        price: parseFloat(e.price) || 0,
        default: !!e.default,
        group: e.group || null,
      }));
      const dupIds = cleanedExtras.map((e) => e.id).filter((id, i, arr) => id && arr.indexOf(id) !== i);
      if (dupIds.length) {
        toast.error(`Duplicate extra IDs: ${dupIds.join(", ")}`);
        setBusy(false);
        return;
      }
      const missing = cleanedExtras.find((e) => !e.id || !e.label);
      if (missing) {
        toast.error("Every add-on needs both an ID and a label");
        setBusy(false);
        return;
      }
      const payload = { ...form, extras: cleanedExtras };
      if (creating) {
        await api.post("/inventory", payload);
        toast.success("Item created");
      } else {
        await api.patch(`/inventory/${item.id}`, payload);
        toast.success("Item updated");
      }
      onSaved();
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const addExtra = () => {
    const newExtra = {
      id: `extra-${Date.now().toString(36)}`,
      label: "",
      description: "",
      price_type: "per_unit_per_day",
      price: 0,
      default: false,
      group: "",
    };
    setForm({ ...form, extras: [...(form.extras || []), newExtra] });
  };

  const updateExtra = (idx, patch) => {
    const next = [...(form.extras || [])];
    next[idx] = { ...next[idx], ...patch };
    setForm({ ...form, extras: next });
  };

  const removeExtra = (idx) => {
    const next = [...(form.extras || [])];
    next.splice(idx, 1);
    setForm({ ...form, extras: next });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-3xl bg-white rounded-[24px] max-h-[90vh] overflow-y-auto"
        data-testid="equipment-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-brand-blue font-black uppercase tracking-tightest text-2xl">
            {creating ? "New Equipment" : `Edit · ${item?.name || ""}`}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          {/* Core fields */}
          <div>
            <p className="text-[10px] font-black uppercase tracking-[0.3em] text-brand-red mb-3">
              Core Details
            </p>
            <div className="grid grid-cols-2 gap-3">
              <Input label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testId="eq-name" />
              <Input
                label="Price/day ($)"
                type="number"
                value={form.price_per_day}
                onChange={(v) => setForm({ ...form, price_per_day: parseFloat(v) || 0 })}
                testId="eq-price"
              />
              <Input label="Icon (fa-*)" value={form.icon} onChange={(v) => setForm({ ...form, icon: v })} testId="eq-icon" />
              <div>
                <label className="block text-[10px] font-black uppercase tracking-widest text-brand-blue mb-1">
                  Category
                </label>
                <select
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm font-bold focus:outline-none focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/10"
                  data-testid="eq-category"
                >
                  <option value="Standard">Standard</option>
                  <option value="Premium">Premium</option>
                </select>
              </div>
              <Input
                label="Stock Total"
                type="number"
                value={form.stock_total}
                onChange={(v) => setForm({ ...form, stock_total: parseInt(v) || 0 })}
                testId="eq-stock"
              />
              <Input
                label="Sort Order"
                type="number"
                value={form.sort_order}
                onChange={(v) => setForm({ ...form, sort_order: parseInt(v) || 0 })}
                testId="eq-sort"
              />
              <div className="col-span-2">
                <Input label="Image URL" value={form.image_url || ""} onChange={(v) => setForm({ ...form, image_url: v })} testId="eq-image" />
              </div>
              <div className="col-span-2">
                <Input
                  label="Description"
                  value={form.description}
                  onChange={(v) => setForm({ ...form, description: v })}
                  testId="eq-description"
                />
              </div>
              <div className="col-span-2 flex items-center gap-3 bg-slate-50 rounded-xl px-3 py-2">
                <input
                  type="checkbox"
                  checked={!!form.instant_booking}
                  onChange={(e) => setForm({ ...form, instant_booking: e.target.checked })}
                  className="accent-brand-blue w-4 h-4"
                  data-testid="eq-instant-booking"
                  id="eq-instant-booking"
                />
                <label htmlFor="eq-instant-booking" className="text-[11px] font-bold text-slate-600">
                  <span className="text-brand-blue font-black uppercase tracking-widest text-[10px]">
                    Instant Booking
                  </span>
                  {" "}— gear is grab-and-go from the Pod (no manual approval).
                </label>
              </div>
            </div>
          </div>

          {/* Extras editor */}
          <div>
            <div className="flex items-end justify-between mb-3">
              <div>
                <p className="text-[10px] font-black uppercase tracking-[0.3em] text-brand-red">
                  Customizations / Add-Ons
                </p>
                <p className="text-[11px] font-bold text-slate-500 mt-0.5">
                  Tent weights, linens, setup fees — anything customer can opt into per item.
                </p>
              </div>
              <button
                onClick={addExtra}
                className="bg-brand-gold text-brand-blue px-4 py-2 rounded-lg font-black uppercase text-[10px] tracking-widest hover:bg-amber-300 transition-colors"
                data-testid="eq-add-extra-button"
              >
                <i className="fas fa-plus mr-2" /> Add Add-On
              </button>
            </div>

            {(form.extras || []).length === 0 ? (
              <div className="bg-slate-50 border border-dashed border-slate-200 rounded-xl px-4 py-6 text-center">
                <i className="fas fa-puzzle-piece text-2xl text-slate-300 mb-2" />
                <p className="text-[11px] font-bold text-slate-500">
                  No add-ons yet — customers will only be able to rent the base item.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {form.extras.map((ex, idx) => (
                  <ExtraRow
                    key={idx}
                    extra={ex}
                    idx={idx}
                    onChange={(patch) => updateExtra(idx, patch)}
                    onRemove={() => removeExtra(idx)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        <button
          onClick={save}
          disabled={busy || !form.name}
          className="btn-primary w-full py-3 rounded-xl font-black uppercase text-xs tracking-widest mt-2 disabled:opacity-50"
          data-testid="eq-save-button"
        >
          {busy ? <><i className="fas fa-spinner fa-spin mr-2" /> Saving</> : <>Save</>}
        </button>
      </DialogContent>
    </Dialog>
  );
}

const PRICE_TYPES = [
  { id: "per_unit_per_day", label: "Per item · per day" },
  { id: "per_unit_flat", label: "Per item · one-time" },
  { id: "flat_per_day", label: "Flat · per day" },
  { id: "flat", label: "Flat · one-time" },
];

function ExtraRow({ extra, idx, onChange, onRemove }) {
  return (
    <div
      className="bg-white rounded-xl border border-slate-200 p-3 space-y-2.5"
      data-testid={`eq-extra-row-${idx}`}
    >
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">
          Add-On #{idx + 1}
        </span>
        <button
          onClick={onRemove}
          className="text-brand-red text-[10px] font-black uppercase tracking-widest hover:text-red-800"
          data-testid={`eq-extra-remove-${idx}`}
        >
          <i className="fas fa-trash mr-1" /> Remove
        </button>
      </div>
      <div className="grid grid-cols-2 gap-2.5">
        <Input
          label="ID (slug)"
          value={extra.id || ""}
          onChange={(v) => onChange({ id: v.replace(/[^a-z0-9-]/gi, "-").toLowerCase() })}
          testId={`eq-extra-${idx}-id`}
        />
        <Input
          label="Label (shown to customer)"
          value={extra.label || ""}
          onChange={(v) => onChange({ label: v })}
          testId={`eq-extra-${idx}-label`}
        />
        <div>
          <label className="block text-[10px] font-black uppercase tracking-widest text-brand-blue mb-1">
            Price Type
          </label>
          <select
            value={extra.price_type || "per_unit_per_day"}
            onChange={(e) => onChange({ price_type: e.target.value })}
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm font-bold focus:outline-none focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/10"
            data-testid={`eq-extra-${idx}-price-type`}
          >
            {PRICE_TYPES.map((t) => (
              <option key={t.id} value={t.id}>
                {t.label}
              </option>
            ))}
          </select>
        </div>
        <Input
          label="Price ($)"
          type="number"
          value={extra.price ?? 0}
          onChange={(v) => onChange({ price: parseFloat(v) || 0 })}
          testId={`eq-extra-${idx}-price`}
        />
        <Input
          label="Group (optional)"
          value={extra.group || ""}
          onChange={(v) => onChange({ group: v })}
          testId={`eq-extra-${idx}-group`}
        />
        <div className="flex items-center gap-2 self-end pb-1">
          <input
            type="checkbox"
            checked={!!extra.default}
            onChange={(e) => onChange({ default: e.target.checked })}
            className="accent-brand-blue w-4 h-4"
            id={`eq-extra-${idx}-default`}
            data-testid={`eq-extra-${idx}-default`}
          />
          <label
            htmlFor={`eq-extra-${idx}-default`}
            className="text-[10px] font-black uppercase tracking-widest text-brand-blue"
          >
            Pre-checked by default
          </label>
        </div>
        <div className="col-span-2">
          <Input
            label="Description (optional)"
            value={extra.description || ""}
            onChange={(v) => onChange({ description: v })}
            testId={`eq-extra-${idx}-description`}
          />
        </div>
      </div>
    </div>
  );
}

function Input({ label, value, onChange, type = "text", testId }) {
  return (
    <div>
      <label className="block text-[10px] font-black uppercase tracking-widest text-brand-blue mb-1">
        {label}
      </label>
      <input
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm font-bold focus:outline-none focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/10"
        data-testid={testId}
      />
    </div>
  );
}


const ID_KINDS = [
  { id: "front", label: "License Front", field: "id_document_path", icon: "fa-id-card" },
  { id: "back", label: "License Back", field: "id_document_back_path", icon: "fa-id-badge" },
  { id: "selfie", label: "Selfie With ID", field: "selfie_path", icon: "fa-user-shield" },
];

function CustomerIDsSection() {
  const [customers, setCustomers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("all"); // "all" | "verified" | "pending"
  const [query, setQuery] = useState("");
  const [openCustomer, setOpenCustomer] = useState(null);

  const refresh = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/admin/customers");
      setCustomers(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load customers");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const filtered = customers.filter((c) => {
    if (filter === "verified" && !c.id_verified) return false;
    if (filter === "pending" && c.id_verified) return false;
    if (query) {
      const q = query.toLowerCase();
      if (!c.email.toLowerCase().includes(q) && !(c.name || "").toLowerCase().includes(q)) {
        return false;
      }
    }
    return true;
  });

  const verifiedCount = customers.filter((c) => c.id_verified).length;
  const pendingCount = customers.length - verifiedCount;

  return (
    <div className="space-y-6" data-testid="admin-ids-section">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-3">
        <div>
          <p className="text-[10px] font-black uppercase tracking-[0.4em] text-brand-red mb-1.5">
            Identity Vault
          </p>
          <h2 className="text-3xl font-black uppercase tracking-tightest text-brand-blue">
            Customer ID Documents
          </h2>
          <p className="text-slate-500 font-bold text-sm mt-1.5">
            Side-by-side view of every renter's verification photos.
          </p>
        </div>
        <button
          onClick={refresh}
          disabled={busy}
          className="bg-slate-100 text-brand-blue px-4 py-2 rounded-xl font-black uppercase text-[10px] tracking-widest border border-slate-200 hover:bg-brand-blue hover:text-white hover:border-brand-blue transition-colors disabled:opacity-50"
          data-testid="admin-ids-refresh"
        >
          <i className={`fas fa-rotate mr-2 ${busy ? "fa-spin" : ""}`} /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <Stat label="Total" value={customers.length} icon="fa-users" />
        <Stat label="Verified" value={verifiedCount} icon="fa-circle-check" tone="emerald" />
        <Stat label="Pending" value={pendingCount} icon="fa-circle-exclamation" tone="amber" />
      </div>

      <div className="bg-white rounded-[24px] border border-slate-100 shadow-sm p-4 flex flex-col md:flex-row gap-3">
        <div className="flex-1">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search name or email…"
            className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-bold focus:outline-none focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/10"
            data-testid="admin-ids-search"
          />
        </div>
        <div className="flex gap-2">
          {[
            { id: "all", label: "All" },
            { id: "verified", label: "Verified" },
            { id: "pending", label: "Pending" },
          ].map((f) => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              className={`px-4 py-2.5 rounded-xl font-black uppercase text-[10px] tracking-widest transition-colors ${
                filter === f.id
                  ? "bg-brand-blue text-white"
                  : "bg-slate-100 text-brand-blue hover:bg-slate-200"
              }`}
              data-testid={`admin-ids-filter-${f.id}`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {busy && customers.length === 0 ? (
        <p className="text-slate-500 font-bold text-center py-10">Loading customers…</p>
      ) : filtered.length === 0 ? (
        <div
          className="bg-white rounded-[24px] border border-dashed border-slate-200 px-6 py-16 text-center"
          data-testid="admin-ids-empty"
        >
          <i className="fas fa-id-card text-4xl text-slate-300 mb-3" />
          <p className="font-black uppercase tracking-tightest text-brand-blue">No customers</p>
          <p className="text-xs text-slate-500 font-bold mt-1">Try changing the filter or search.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((c) => (
            <CustomerIDCard key={c.id} customer={c} onOpen={() => setOpenCustomer(c)} />
          ))}
        </div>
      )}

      <CustomerIDDialog
        customer={openCustomer}
        onClose={() => setOpenCustomer(null)}
      />
    </div>
  );
}

function Stat({ label, value, icon, tone = "blue" }) {
  const palette = {
    blue: "bg-brand-blue text-brand-gold",
    emerald: "bg-emerald-100 text-emerald-700",
    amber: "bg-amber-100 text-amber-700",
  }[tone];
  return (
    <div className="bg-white rounded-[20px] border border-slate-100 shadow-sm p-4 flex items-center gap-3">
      <div className={`w-11 h-11 rounded-2xl flex items-center justify-center ${palette}`}>
        <i className={`fas ${icon}`} />
      </div>
      <div>
        <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">{label}</p>
        <p className="text-2xl font-black text-brand-blue">{value}</p>
      </div>
    </div>
  );
}

function CustomerIDCard({ customer, onOpen }) {
  const verified = customer.id_verified;
  const missing = ID_KINDS.filter((k) => !customer[k.field]).map((k) => k.label);
  return (
    <button
      onClick={onOpen}
      className="w-full text-left bg-white rounded-[24px] border border-slate-100 hover:border-brand-blue shadow-sm hover:shadow-md transition-all p-4 flex flex-col md:flex-row items-start md:items-center gap-4"
      data-testid={`admin-customer-row-${customer.id}`}
    >
      <div
        className={`w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 ${
          verified ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"
        }`}
      >
        <i className={`fas ${verified ? "fa-circle-check" : "fa-clock"} text-lg`} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-black text-sm uppercase text-brand-blue tracking-tightest">
          {customer.name || "—"}
        </p>
        <p className="text-[11px] font-bold text-slate-500 truncate">{customer.email}</p>
        {customer.phone && (
          <p className="text-[11px] font-bold text-slate-400 mt-0.5">
            <i className="fas fa-phone text-[9px] mr-1.5" />
            {customer.phone}
          </p>
        )}
      </div>
      <div className="flex gap-2 flex-shrink-0">
        {ID_KINDS.map((k) => (
          <div
            key={k.id}
            className={`w-9 h-9 rounded-lg flex items-center justify-center ${
              customer[k.field]
                ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                : "bg-slate-100 text-slate-400 border border-dashed border-slate-300"
            }`}
            title={k.label}
          >
            <i className={`fas ${k.icon} text-[11px]`} />
          </div>
        ))}
      </div>
      <div className="flex flex-col items-end gap-1.5 flex-shrink-0 min-w-[110px]">
        <span
          className={`px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest ${
            verified ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"
          }`}
        >
          {verified ? "Verified" : "Pending"}
        </span>
        <span className="text-[10px] font-bold text-slate-500">
          {customer.booking_count} {customer.booking_count === 1 ? "rental" : "rentals"}
        </span>
        {!verified && missing.length > 0 && (
          <span className="text-[9px] font-bold text-brand-red">
            Missing: {missing.length}
          </span>
        )}
      </div>
    </button>
  );
}

function CustomerIDDialog({ customer, onClose }) {
  if (!customer) return null;
  return (
    <Dialog open={!!customer} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-5xl bg-white rounded-[32px] p-0 overflow-hidden"
        data-testid="admin-customer-dialog"
      >
        <DialogHeader className="bg-brand-blue text-white px-6 py-5 m-0">
          <DialogTitle className="text-white font-black uppercase tracking-tightest text-2xl">
            {customer.name}
          </DialogTitle>
          <p className="text-[10px] font-black uppercase tracking-widest text-brand-gold">
            {customer.email}
            {customer.phone ? ` • ${customer.phone}` : ""}
          </p>
        </DialogHeader>
        <div className="px-6 py-6 max-h-[75vh] overflow-y-auto">
          <div className="grid md:grid-cols-3 gap-4">
            {ID_KINDS.map((k) => (
              <DocPanel
                key={k.id}
                kind={k}
                userId={customer.id}
                hasFile={!!customer[k.field]}
              />
            ))}
          </div>

          <div className="mt-6 grid sm:grid-cols-3 gap-3 text-xs font-bold">
            <div className="bg-slate-50 rounded-xl p-3">
              <p className="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-1">
                Total Rentals
              </p>
              <p className="text-lg font-black text-brand-blue">{customer.booking_count}</p>
            </div>
            <div className="bg-slate-50 rounded-xl p-3">
              <p className="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-1">
                Damage Score
              </p>
              <p className="text-lg font-black text-brand-blue">{customer.damage_score}</p>
            </div>
            <div className="bg-slate-50 rounded-xl p-3">
              <p className="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-1">
                Account Status
              </p>
              <p
                className={`text-lg font-black ${
                  customer.is_blocked ? "text-brand-red" : "text-emerald-600"
                }`}
              >
                {customer.is_blocked ? "Suspended" : "Active"}
              </p>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function DocPanel({ kind, userId, hasFile }) {
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!hasFile) return;
    let cancelled = false;
    let createdUrl = null;
    setLoading(true);
    setError(null);
    api
      .get(`/admin/customers/${userId}/id-document?kind=${kind.id}`, { responseType: "blob" })
      .then((res) => {
        if (cancelled) return;
        createdUrl = URL.createObjectURL(res.data);
        setUrl(createdUrl);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(formatApiErrorDetail(e.response?.data?.detail) || "Could not load document");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [kind.id, userId, hasFile]);

  return (
    <div
      className={`rounded-2xl border-2 overflow-hidden ${
        hasFile ? "border-emerald-200" : "border-dashed border-slate-200"
      }`}
      data-testid={`admin-doc-panel-${kind.id}`}
    >
      <div className="bg-slate-50 px-4 py-2.5 flex items-center justify-between border-b border-slate-100">
        <p className="text-[10px] font-black uppercase tracking-widest text-brand-blue flex items-center gap-2">
          <i className={`fas ${kind.icon}`} /> {kind.label}
        </p>
        {hasFile ? (
          <i className="fas fa-circle-check text-emerald-600 text-xs" />
        ) : (
          <i className="fas fa-circle-exclamation text-slate-400 text-xs" />
        )}
      </div>
      <div className="aspect-[4/3] bg-slate-100 flex items-center justify-center">
        {!hasFile ? (
          <div className="text-center px-4">
            <i className="fas fa-image text-3xl text-slate-300 mb-2" />
            <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">
              Not Uploaded
            </p>
          </div>
        ) : loading ? (
          <i className="fas fa-spinner fa-spin text-2xl text-slate-400" />
        ) : error ? (
          <p className="text-[10px] font-black text-brand-red text-center px-4">{error}</p>
        ) : url ? (
          <img
            src={url}
            alt={kind.label}
            className="w-full h-full object-contain bg-white"
            data-testid={`admin-doc-img-${kind.id}`}
          />
        ) : null}
      </div>
    </div>
  );
}



const AUDIENCE_OPTIONS = [
  { id: "all", label: "All opted-in customers" },
  { id: "verified", label: "ID-verified only" },
  { id: "lapsed_60d", label: "Lapsed 60+ days (win-back)" },
  { id: "by_event", label: "By event type" },
];

const EVENT_TYPE_OPTIONS = [
  "wedding", "birthday", "graduation", "corporate",
  "church-school", "bbq-backyard", "dinner-party", "house-party", "other",
];

function NewslettersSection() {
  const [list, setList] = useState([]);
  const [autoEnabled, setAutoEnabled] = useState(true);
  const [nextRun, setNextRun] = useState("");
  const [audienceCount, setAudienceCount] = useState(0);
  const [history, setHistory] = useState([]);
  const [previewMonth, setPreviewMonth] = useState(null);
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewSubject, setPreviewSubject] = useState("");
  const [busy, setBusy] = useState(false);
  const [autoBusy, setAutoBusy] = useState(false);
  const [sendOpen, setSendOpen] = useState(null);

  const refresh = () =>
    api.get("/admin/newsletters").then(({ data }) => {
      setList(data.items || data || []);
      setAutoEnabled(data.auto_send_enabled !== false);
      setNextRun(data.next_auto_send || "");
      setAudienceCount(data.audience_count || 0);
      setHistory(data.history || []);
    });

  useEffect(() => { refresh(); }, []);

  const toggleAuto = async () => {
    setAutoBusy(true);
    try {
      const next = !autoEnabled;
      await api.patch("/admin/newsletters/settings", { auto_newsletter_enabled: next });
      setAutoEnabled(next);
      toast.success(next ? "Auto-send ON — newsletter blasts on the 1st of each month" : "Auto-send PAUSED");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Toggle failed");
    } finally {
      setAutoBusy(false);
    }
  };

  const runNow = async () => {
    if (!confirm("Send THIS month's newsletter to all opted-in customers right now? (Idempotent — already-sent months are skipped.)")) return;
    setAutoBusy(true);
    try {
      await api.post("/admin/newsletters/run-cron-now");
      toast.success("Cron triggered — check Broadcasts log for results");
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Trigger failed");
    } finally {
      setAutoBusy(false);
    }
  };

  const openPreview = async (m) => {
    setBusy(true);
    setPreviewMonth(m);
    try {
      const { data } = await api.get(`/admin/newsletters/${m}/preview`);
      setPreviewSubject(data.subject);
      setPreviewHtml(data.html);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Preview failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="space-y-6" data-testid="admin-newsletters-section">
      <div>
        <p className="text-[10px] font-black uppercase tracking-[0.4em] text-brand-red mb-1.5">
          Year-Round Drip
        </p>
        <h2 className="text-3xl font-black uppercase tracking-tightest text-brand-blue">
          Monthly Newsletters
        </h2>
        <p className="text-slate-500 font-bold text-sm mt-1.5 max-w-3xl">
          12 pre-written templates. Each month: anchor holidays with 2–3 event ideas + rental
          suggestions, gear cards pulled live from inventory, and trending callouts from the prior
          month. Auto-sends on the 1st of each month — toggle below.
        </p>
      </div>

      {/* Auto-send toggle */}
      <div
        className={`rounded-[24px] p-5 flex flex-col md:flex-row items-start md:items-center gap-4 border-2 transition-colors ${
          autoEnabled ? "bg-brand-blue text-white border-brand-blue" : "bg-white border-slate-200"
        }`}
        data-testid="newsletter-auto-card"
      >
        <div
          className={`w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 ${
            autoEnabled ? "bg-brand-gold text-brand-blue" : "bg-slate-100 text-slate-400"
          }`}
        >
          <i className={`fas ${autoEnabled ? "fa-rocket" : "fa-pause"} text-lg`} />
        </div>
        <div className="flex-1">
          <p className={`text-[10px] font-black uppercase tracking-[0.3em] ${autoEnabled ? "text-brand-gold" : "text-brand-red"}`}>
            {autoEnabled ? "Auto-Send ON" : "Auto-Send PAUSED"}
          </p>
          <p className={`text-base md:text-lg font-black uppercase tracking-tightest mt-0.5 ${autoEnabled ? "text-white" : "text-brand-blue"}`}>
            Monthly newsletter cron
          </p>
          <p className={`text-[11px] font-medium mt-0.5 leading-snug ${autoEnabled ? "text-white/80" : "text-slate-500"}`}>
            {autoEnabled
              ? <>Next blast: <strong>{nextRun || "1st of next month"}</strong> → <strong className={autoEnabled ? "text-brand-gold" : "text-brand-red"}>{audienceCount}</strong> opted-in customer{audienceCount === 1 ? "" : "s"}.</>
              : <>Auto-send is paused. <strong>{audienceCount}</strong> customer{audienceCount === 1 ? "" : "s"} on the list when you re-enable.</>
            }
          </p>
        </div>
        <div className="flex gap-2 flex-shrink-0">
          <button
            onClick={runNow}
            disabled={autoBusy}
            className={`px-4 py-2.5 rounded-xl font-black uppercase text-[10px] tracking-widest transition-colors disabled:opacity-50 ${
              autoEnabled ? "bg-white text-brand-blue hover:bg-brand-gold" : "bg-slate-100 text-brand-blue hover:bg-brand-gold"
            }`}
            data-testid="newsletter-run-now-button"
          >
            <i className="fas fa-bolt mr-1.5" /> Run Now
          </button>
          <button
            onClick={toggleAuto}
            disabled={autoBusy}
            className={`px-4 py-2.5 rounded-xl font-black uppercase text-[10px] tracking-widest transition-colors disabled:opacity-50 ${
              autoEnabled ? "bg-brand-red text-white hover:bg-red-700" : "btn-primary"
            }`}
            data-testid="newsletter-auto-toggle"
          >
            {autoBusy ? (
              <><i className="fas fa-spinner fa-spin" /></>
            ) : autoEnabled ? (
              <><i className="fas fa-pause mr-1.5" /> Pause</>
            ) : (
              <><i className="fas fa-play mr-1.5" /> Enable</>
            )}
          </button>
        </div>
      </div>

      {/* Last 12 sends timeline */}
      <div data-testid="newsletter-history">
        <p className="text-[10px] font-black uppercase tracking-[0.3em] text-brand-red mb-2">
          Last 12 Sends
        </p>
        {history.length === 0 ? (
          <div className="bg-white rounded-2xl border border-dashed border-slate-200 px-5 py-6">
            <p className="text-[11px] font-bold text-slate-500 text-center">
              <i className="fas fa-clock mr-1.5" /> No newsletters sent yet — first auto-blast on the 1st.
            </p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {history.map((h) => {
              const sent = h.email_result?.sent || 0;
              const failed = h.email_result?.failed || 0;
              const total = h.recipients_email || 0;
              const auto = h.kind === "monthly_newsletter_auto";
              const success = sent > 0 && failed === 0;
              const partial = sent > 0 && failed > 0;
              return (
                <div
                  key={h.id}
                  className="bg-white rounded-2xl border border-slate-100 p-3 flex items-center gap-3"
                  data-testid={`newsletter-history-${h.id}`}
                >
                  <div
                    className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${
                      success ? "bg-emerald-100 text-emerald-700"
                        : partial ? "bg-amber-100 text-amber-700"
                        : "bg-rose-100 text-rose-700"
                    }`}
                  >
                    <i className={`fas ${success ? "fa-check" : partial ? "fa-triangle-exclamation" : "fa-xmark"} text-xs`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-black text-[11px] uppercase text-brand-blue tracking-tightest">
                      {h.month_year_key || `Month ${h.month}`}
                      {auto && <span className="ml-2 text-[8px] tracking-widest text-brand-gold bg-brand-blue px-1.5 py-0.5 rounded">AUTO</span>}
                    </p>
                    <p className="text-[10px] font-bold text-slate-500 mt-0.5">
                      {new Date(h.created_at).toLocaleDateString()} · {sent}/{total} sent
                      {failed > 0 && <span className="text-brand-red"> · {failed} failed</span>}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {list.map((n) => (
          <div
            key={n.month}
            className="bg-white rounded-[24px] border border-slate-100 shadow-sm p-5 flex flex-col"
            data-testid={`newsletter-card-${n.month}`}
          >
            <p className="text-[9px] font-black uppercase tracking-[0.3em] text-brand-red">
              {n.month_label} · #{n.month}
            </p>
            <p className="font-black text-sm uppercase text-brand-blue tracking-tightest mt-1.5 leading-tight">
              {n.subject}
            </p>
            <p className="text-[11px] font-bold text-slate-500 mt-1.5 leading-snug flex-1">
              {n.preheader}
            </p>
            <div className="flex flex-wrap gap-1.5 mt-3">
              {(n.recommended_items || []).slice(0, 4).map((r) => (
                <span
                  key={r}
                  className="text-[9px] font-black uppercase tracking-widest bg-slate-100 text-slate-600 px-2 py-1 rounded"
                >
                  {r}
                </span>
              ))}
            </div>
            <div className="flex gap-2 mt-4">
              <button
                onClick={() => openPreview(n.month)}
                className="flex-1 bg-slate-100 text-brand-blue py-2 rounded-lg font-black uppercase text-[10px] tracking-widest hover:bg-brand-gold transition-colors"
                data-testid={`newsletter-preview-${n.month}`}
              >
                Preview
              </button>
              <button
                onClick={() => setSendOpen(n)}
                className="flex-1 btn-primary py-2 rounded-lg font-black uppercase text-[10px] tracking-widest"
                data-testid={`newsletter-send-${n.month}`}
              >
                <i className="fas fa-paper-plane mr-1.5" /> Send
              </button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={previewMonth !== null} onOpenChange={(o) => !o && setPreviewMonth(null)}>
        <DialogContent className="max-w-3xl bg-white rounded-[28px] p-0 overflow-hidden" data-testid="newsletter-preview-dialog">
          <DialogHeader className="bg-brand-blue text-white px-6 py-4">
            <DialogTitle className="text-white font-black uppercase tracking-tightest text-xl">
              Preview · {previewSubject}
            </DialogTitle>
          </DialogHeader>
          <div className="bg-slate-100 max-h-[75vh] overflow-y-auto">
            {busy ? (
              <div className="p-12 text-center">
                <i className="fas fa-spinner fa-spin text-2xl text-slate-400" />
              </div>
            ) : (
              <div dangerouslySetInnerHTML={{ __html: previewHtml }} />
            )}
          </div>
        </DialogContent>
      </Dialog>

      <NewsletterSendDialog newsletter={sendOpen} onClose={() => setSendOpen(null)} />
    </section>
  );
}

function NewsletterSendDialog({ newsletter, onClose }) {
  const [audience, setAudience] = useState("all");
  const [eventType, setEventType] = useState("wedding");
  const [testEmail, setTestEmail] = useState("");
  const [busy, setBusy] = useState(false);

  if (!newsletter) return null;

  const send = async (testOnly) => {
    setBusy(true);
    try {
      const payload = {
        month: newsletter.month,
        audience,
        event_type_filter: audience === "by_event" ? eventType : null,
        test_only_email: testOnly ? testEmail : null,
      };
      const { data } = await api.post("/admin/newsletters/send", payload);
      if (testOnly) {
        if (data.result?.sent) {
          toast.success(`Test sent to ${testEmail}`);
        } else {
          toast.error(`Test failed: ${data.result?.body || data.result?.error || data.result?.reason}`);
        }
      } else {
        toast.success(`Newsletter blasted: ${data.email_result?.sent} sent / ${data.email_result?.failed} failed`);
        onClose();
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Send failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!newsletter} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg bg-white rounded-[24px]" data-testid="newsletter-send-dialog">
        <DialogHeader>
          <DialogTitle className="text-brand-blue font-black uppercase tracking-tightest text-2xl">
            Send {newsletter.month_label} Newsletter
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <label className="block text-[10px] font-black uppercase tracking-widest text-brand-blue mb-2">
              Audience
            </label>
            <select
              value={audience}
              onChange={(e) => setAudience(e.target.value)}
              className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm font-bold focus:outline-none focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/10"
              data-testid="newsletter-audience"
            >
              {AUDIENCE_OPTIONS.map((o) => (
                <option key={o.id} value={o.id}>{o.label}</option>
              ))}
            </select>
            {audience === "by_event" && (
              <select
                value={eventType}
                onChange={(e) => setEventType(e.target.value)}
                className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm font-bold focus:outline-none focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/10"
                data-testid="newsletter-event-filter"
              >
                {EVENT_TYPE_OPTIONS.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            )}
          </div>
          <div className="bg-slate-50 rounded-xl p-3">
            <label className="block text-[10px] font-black uppercase tracking-widest text-brand-blue mb-2">
              Test Send (single email first)
            </label>
            <div className="flex gap-2">
              <input
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="you@eventism.com"
                className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm font-bold focus:outline-none focus:border-brand-blue"
                data-testid="newsletter-test-email"
              />
              <button
                onClick={() => send(true)}
                disabled={busy || !testEmail}
                className="bg-slate-200 text-brand-blue px-4 py-2 rounded-lg font-black uppercase text-[10px] tracking-widest hover:bg-brand-gold transition-colors disabled:opacity-50"
                data-testid="newsletter-test-send"
              >
                Test Send
              </button>
            </div>
          </div>
          <button
            onClick={() => send(false)}
            disabled={busy}
            className="btn-primary w-full py-3 rounded-xl font-black uppercase text-xs tracking-widest disabled:opacity-50"
            data-testid="newsletter-blast-button"
          >
            {busy ? <><i className="fas fa-spinner fa-spin mr-2" /> Sending</> : <>Blast to {audience === "by_event" ? eventType : audience} →</>}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function BroadcastsSection() {
  const [history, setHistory] = useState([]);
  const [audience, setAudience] = useState("all");
  const [eventType, setEventType] = useState("wedding");
  const [channel, setChannel] = useState("email");
  const [subject, setSubject] = useState("");
  const [html, setHtml] = useState("");
  const [smsText, setSmsText] = useState("");
  const [busy, setBusy] = useState(false);

  const refresh = () => api.get("/admin/broadcasts").then(({ data }) => setHistory(data));
  useEffect(() => { refresh(); }, []);

  const send = async () => {
    if (!subject || (!html && channel !== "sms") || (channel !== "email" && !smsText)) {
      toast.error("Subject + body (and SMS text if channel includes SMS) are required");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/broadcasts", {
        subject,
        html: html || `<p>${subject}</p>`,
        audience,
        event_type_filter: audience === "by_event" ? eventType : null,
        channel,
        sms_text: channel !== "email" ? smsText : null,
      });
      toast.success(`Broadcast sent — email ${data.email_result?.sent || 0}/${data.recipients_email}, sms ${data.sms_result?.sent || 0}/${data.recipients_sms}`);
      setSubject(""); setHtml(""); setSmsText("");
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Broadcast failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="space-y-6" data-testid="admin-broadcasts-section">
      <div>
        <p className="text-[10px] font-black uppercase tracking-[0.4em] text-brand-red mb-1.5">
          Direct Outreach
        </p>
        <h2 className="text-3xl font-black uppercase tracking-tightest text-brand-blue">
          Broadcasts
        </h2>
        <p className="text-slate-500 font-bold text-sm mt-1.5 max-w-3xl">
          One-off email or SMS blasts. Use for new equipment announcements, weather-driven
          cancellations, holiday schedule changes.
        </p>
      </div>

      <div className="bg-white rounded-[28px] border border-slate-100 shadow-sm p-6 space-y-4">
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-[10px] font-black uppercase tracking-widest text-brand-blue mb-2">Audience</label>
            <select value={audience} onChange={(e) => setAudience(e.target.value)} className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm font-bold" data-testid="broadcast-audience">
              {AUDIENCE_OPTIONS.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
            </select>
            {audience === "by_event" && (
              <select value={eventType} onChange={(e) => setEventType(e.target.value)} className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm font-bold">
                {EVENT_TYPE_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            )}
          </div>
          <div>
            <label className="block text-[10px] font-black uppercase tracking-widest text-brand-blue mb-2">Channel</label>
            <select value={channel} onChange={(e) => setChannel(e.target.value)} className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm font-bold" data-testid="broadcast-channel">
              <option value="email">Email only</option>
              <option value="sms">SMS only</option>
              <option value="both">Email + SMS</option>
            </select>
          </div>
        </div>
        <div>
          <label className="block text-[10px] font-black uppercase tracking-widest text-brand-blue mb-2">Subject</label>
          <input value={subject} onChange={(e) => setSubject(e.target.value)} className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm font-bold" placeholder="New: Patio heaters now in the Pod" data-testid="broadcast-subject" />
        </div>
        {channel !== "sms" && (
          <div>
            <label className="block text-[10px] font-black uppercase tracking-widest text-brand-blue mb-2">Email Body (HTML)</label>
            <textarea value={html} onChange={(e) => setHtml(e.target.value)} rows={6} className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm font-bold font-mono resize-none" placeholder="<p>Hello — quick announcement…</p>" data-testid="broadcast-html" />
          </div>
        )}
        {channel !== "email" && (
          <div>
            <label className="block text-[10px] font-black uppercase tracking-widest text-brand-blue mb-2">SMS Text (max 320 chars)</label>
            <textarea value={smsText} onChange={(e) => setSmsText(e.target.value.slice(0, 320))} rows={3} className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm font-bold resize-none" placeholder="Patio heaters just dropped in the Pod — book before Halloween: partyrentalsexpress.com" data-testid="broadcast-sms" />
            <p className="text-[10px] font-bold text-slate-400 mt-1">{smsText.length}/320</p>
          </div>
        )}
        <button onClick={send} disabled={busy} className="btn-primary w-full py-3.5 rounded-xl font-black uppercase tracking-widest text-sm disabled:opacity-50" data-testid="broadcast-send-button">
          {busy ? <><i className="fas fa-spinner fa-spin mr-2" /> Sending</> : <><i className="fas fa-bullhorn mr-2" /> Send Broadcast</>}
        </button>
      </div>

      <div>
        <h3 className="text-base font-black uppercase tracking-tightest text-brand-blue mb-3">Recent Broadcasts</h3>
        {history.length === 0 ? (
          <p className="text-slate-500 font-bold text-sm">No broadcasts sent yet.</p>
        ) : (
          <div className="space-y-2">
            {history.map((b) => (
              <div key={b.id} className="bg-white rounded-2xl border border-slate-100 p-4 flex items-center justify-between" data-testid={`broadcast-row-${b.id}`}>
                <div className="flex-1 min-w-0">
                  <p className="font-black text-xs uppercase text-brand-blue tracking-tightest truncate">{b.subject}</p>
                  <p className="text-[10px] font-bold text-slate-500 mt-0.5">
                    {new Date(b.created_at).toLocaleString()} · {b.audience} · {b.channel}
                  </p>
                </div>
                <div className="text-right text-[11px] font-black">
                  {b.email_result && <p className="text-emerald-600">Email {b.email_result.sent}/{b.recipients_email}</p>}
                  {b.sms_result && !b.sms_result.skipped && <p className="text-brand-blue">SMS {b.sms_result.sent}/{b.recipients_sms}</p>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function WishlistSection() {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);

  const refresh = () => {
    setBusy(true);
    api.get("/admin/wishlist").then(({ data }) => setItems(data)).finally(() => setBusy(false));
  };
  useEffect(() => { refresh(); }, []);

  return (
    <section className="space-y-6" data-testid="admin-wishlist-section">
      <div className="flex items-end justify-between">
        <div>
          <p className="text-[10px] font-black uppercase tracking-[0.4em] text-brand-red mb-1.5">
            Customer Voice
          </p>
          <h2 className="text-3xl font-black uppercase tracking-tightest text-brand-blue">
            Equipment Wishlist
          </h2>
          <p className="text-slate-500 font-bold text-sm mt-1.5 max-w-3xl">
            What renters are asking us to carry — pulled from profiles + post-rental surveys. Sorted by votes.
          </p>
        </div>
        <button
          onClick={refresh}
          disabled={busy}
          className="bg-slate-100 text-brand-blue px-4 py-2 rounded-xl font-black uppercase text-[10px] tracking-widest border border-slate-200 hover:bg-brand-blue hover:text-white transition-colors disabled:opacity-50"
          data-testid="wishlist-refresh"
        >
          <i className={`fas fa-rotate mr-2 ${busy ? "fa-spin" : ""}`} /> Refresh
        </button>
      </div>

      {items.length === 0 ? (
        <div className="bg-white rounded-[24px] border border-dashed border-slate-200 px-6 py-16 text-center" data-testid="wishlist-empty">
          <i className="fas fa-lightbulb text-4xl text-slate-300 mb-3" />
          <p className="font-black uppercase tracking-tightest text-brand-blue">No wishlist data yet</p>
          <p className="text-xs text-slate-500 font-bold mt-1">Encourage customers to fill the wishlist on their profile or post-rental survey.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((it, idx) => (
            <div key={it.item} className="bg-white rounded-2xl border border-slate-100 p-4 flex items-center gap-4" data-testid={`wishlist-row-${idx}`}>
              <div className="w-10 h-10 rounded-xl bg-brand-gold text-brand-blue flex items-center justify-center font-black">
                {it.votes}
              </div>
              <div className="flex-1">
                <p className="font-black text-sm uppercase text-brand-blue tracking-tightest">{it.item}</p>
                <p className="text-[10px] font-bold text-slate-500 mt-0.5">
                  {it.sources.length} mention{it.sources.length === 1 ? "" : "s"} ·{" "}
                  {Array.from(new Set(it.sources.map((s) => s.from))).join(" + ")}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}



const EVENT_TYPE_LABELS = {
  wedding: "Wedding",
  birthday: "Birthday",
  graduation: "Graduation",
  corporate: "Corporate",
  "church-school": "Church/School",
  "bbq-backyard": "BBQ/Backyard",
  "dinner-party": "Dinner Party",
  "house-party": "House Party",
  other: "Other",
};

function CRMSection() {
  const [customers, setCustomers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [query, setQuery] = useState("");
  const [eventFilter, setEventFilter] = useState("all");
  const [openCustomerId, setOpenCustomerId] = useState(null);

  const refresh = () => {
    setBusy(true);
    api.get("/admin/customers").then(({ data }) => setCustomers(data)).finally(() => setBusy(false));
  };
  useEffect(() => { refresh(); }, []);

  const filtered = customers.filter((c) => {
    if (eventFilter !== "all" && !(c.event_types || []).includes(eventFilter)) return false;
    if (query) {
      const q = query.toLowerCase();
      if (![c.email, c.name, c.phone, c.service_address].some((s) => (s || "").toLowerCase().includes(q))) return false;
    }
    return true;
  });

  const totalLTV = customers.reduce((s, c) => s + (c.lifetime_value || 0), 0);
  const repeat = customers.filter((c) => (c.rental_count || 0) >= 2).length;
  const optedIn = customers.filter((c) => c.newsletter_opt_in).length;

  return (
    <section className="space-y-6" data-testid="admin-crm-section">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-3">
        <div>
          <p className="text-[10px] font-black uppercase tracking-[0.4em] text-brand-red mb-1.5">
            Customer Relationship Manager
          </p>
          <h2 className="text-3xl font-black uppercase tracking-tightest text-brand-blue">
            Customers (CRM)
          </h2>
          <p className="text-slate-500 font-bold text-sm mt-1.5 max-w-3xl">
            Every renter, with lifetime value, distance from the Pod, event types, opt-ins,
            and full booking + survey history. Click a row for the 360 view.
          </p>
        </div>
        <button
          onClick={refresh}
          disabled={busy}
          className="bg-slate-100 text-brand-blue px-4 py-2 rounded-xl font-black uppercase text-[10px] tracking-widest border border-slate-200 hover:bg-brand-blue hover:text-white transition-colors disabled:opacity-50 self-start"
          data-testid="crm-refresh"
        >
          <i className={`fas fa-rotate mr-2 ${busy ? "fa-spin" : ""}`} /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat label="Total Customers" value={customers.length} icon="fa-users" />
        <Stat label="Lifetime Revenue" value={`$${totalLTV.toFixed(0)}`} icon="fa-dollar-sign" tone="emerald" />
        <Stat label="Repeat Renters" value={repeat} icon="fa-rotate-right" tone="blue" />
        <Stat label="Newsletter Opted-In" value={optedIn} icon="fa-envelope" tone="amber" />
      </div>

      <div className="bg-white rounded-[24px] border border-slate-100 shadow-sm p-4 flex flex-col md:flex-row gap-3">
        <div className="flex-1">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search name, email, phone, or address…"
            className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-bold focus:outline-none focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/10"
            data-testid="crm-search"
          />
        </div>
        <select
          value={eventFilter}
          onChange={(e) => setEventFilter(e.target.value)}
          className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-bold focus:outline-none focus:border-brand-blue"
          data-testid="crm-event-filter"
        >
          <option value="all">All event types</option>
          {Object.entries(EVENT_TYPE_LABELS).map(([id, label]) => (
            <option key={id} value={id}>{label}</option>
          ))}
        </select>
      </div>

      {filtered.length === 0 && !busy ? (
        <div className="bg-white rounded-[24px] border border-dashed border-slate-200 px-6 py-16 text-center" data-testid="crm-empty">
          <i className="fas fa-users-slash text-4xl text-slate-300 mb-3" />
          <p className="font-black uppercase tracking-tightest text-brand-blue">No matches</p>
          <p className="text-xs text-slate-500 font-bold mt-1">Try changing the filter or search.</p>
        </div>
      ) : (
        <div className="bg-white rounded-[24px] border border-slate-100 shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[10px] font-black uppercase tracking-widest text-slate-500">
              <tr>
                <th className="px-5 py-3 text-left">Customer</th>
                <th className="px-3 py-3 text-left">Distance</th>
                <th className="px-3 py-3 text-left">Events</th>
                <th className="px-3 py-3 text-right">Rentals</th>
                <th className="px-3 py-3 text-right">LTV</th>
                <th className="px-3 py-3 text-right">Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => (
                <tr
                  key={c.id}
                  onClick={() => setOpenCustomerId(c.id)}
                  className="border-t border-slate-50 cursor-pointer hover:bg-brand-blue/5 transition-colors"
                  data-testid={`crm-row-${c.id}`}
                >
                  <td className="px-5 py-3">
                    <p className="font-black text-brand-blue tracking-tightest text-xs uppercase">{c.name || "—"}</p>
                    <p className="text-[10px] font-bold text-slate-500">{c.email}</p>
                    {c.phone && <p className="text-[10px] font-bold text-slate-400">{c.phone}</p>}
                  </td>
                  <td className="px-3 py-3 text-[11px] font-bold text-slate-600">
                    {c.distance_miles_from_pod != null ? `${c.distance_miles_from_pod} mi` : <span className="text-slate-300">—</span>}
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex flex-wrap gap-1 max-w-[160px]">
                      {(c.event_types || []).slice(0, 3).map((e) => (
                        <span key={e} className="text-[9px] font-black uppercase bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">
                          {EVENT_TYPE_LABELS[e] || e}
                        </span>
                      ))}
                      {(c.event_types || []).length === 0 && <span className="text-slate-300 text-[10px]">—</span>}
                    </div>
                  </td>
                  <td className="px-3 py-3 text-right text-xs font-black text-brand-blue">{c.rental_count || 0}</td>
                  <td className="px-3 py-3 text-right text-xs font-black text-brand-red">${(c.lifetime_value || 0).toFixed(0)}</td>
                  <td className="px-3 py-3 text-right">
                    <div className="inline-flex items-center gap-1.5">
                      <i className={`fas ${c.id_verified ? "fa-circle-check text-emerald-500" : "fa-circle text-slate-300"} text-[10px]`} title={c.id_verified ? "ID verified" : "ID not verified"} />
                      <i className={`fas fa-envelope text-[10px] ${c.newsletter_opt_in ? "text-brand-blue" : "text-slate-300"}`} title={c.newsletter_opt_in ? "Newsletter opted-in" : "Newsletter opted-out"} />
                      <i className={`fas fa-mobile-alt text-[10px] ${c.sms_opt_in ? "text-brand-blue" : "text-slate-300"}`} title={c.sms_opt_in ? "SMS opted-in" : "SMS opted-out"} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <CRM360Dialog customerId={openCustomerId} onClose={() => setOpenCustomerId(null)} onUpdated={refresh} />
    </section>
  );
}

function CRM360Dialog({ customerId, onClose, onUpdated }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!customerId) { setData(null); return; }
    setBusy(true);
    api.get(`/admin/customers/${customerId}`).then(({ data }) => setData(data)).finally(() => setBusy(false));
  }, [customerId]);

  const block = async (blocked) => {
    if (!data) return;
    if (!confirm(blocked ? "Block this customer? They won't be able to book." : "Unblock this customer?")) return;
    await api.patch(`/admin/customers/${customerId}/block?blocked=${blocked}`);
    toast.success(blocked ? "Blocked" : "Unblocked");
    onUpdated?.();
    onClose();
  };

  if (!customerId) return null;
  const u = data?.user;
  const avgRating = data?.surveys?.length
    ? (data.surveys.reduce((s, x) => s + (x.rating || 0), 0) / data.surveys.length).toFixed(1)
    : null;

  return (
    <Dialog open={!!customerId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl bg-white rounded-[28px] p-0 overflow-hidden" data-testid="crm-360-dialog">
        <DialogHeader className="bg-brand-blue text-white px-6 py-5">
          <DialogTitle className="text-white font-black uppercase tracking-tightest text-2xl">
            {u?.name || "Customer"}
          </DialogTitle>
          <p className="text-[10px] font-black uppercase tracking-widest text-brand-gold">
            {u?.email}{u?.phone ? ` • ${u.phone}` : ""}{u?.is_blocked ? " • SUSPENDED" : ""}
          </p>
        </DialogHeader>
        <div className="p-6 max-h-[78vh] overflow-y-auto space-y-5">
          {busy || !u ? (
            <div className="text-center py-12">
              <i className="fas fa-spinner fa-spin text-2xl text-slate-300" />
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <Stat label="Lifetime Value" value={`$${(data.lifetime_value || 0).toFixed(0)}`} icon="fa-dollar-sign" tone="emerald" />
                <Stat label="Total Rentals" value={data.rental_count || 0} icon="fa-receipt" tone="blue" />
                <Stat label="Avg Rating" value={avgRating || "—"} icon="fa-star" tone="amber" />
                <Stat label="Distance" value={u.distance_miles_from_pod != null ? `${u.distance_miles_from_pod} mi` : "—"} icon="fa-location-dot" />
              </div>

              <div className="bg-slate-50 rounded-2xl p-4 grid sm:grid-cols-2 gap-4">
                <Field label="Address" value={u.service_address || "—"} />
                <Field label="Phone" value={u.phone || "—"} />
                <Field label="Joined" value={u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"} />
                <Field label="Damage Score" value={u.damage_score || 0} />
                <div className="sm:col-span-2">
                  <p className="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-1">Event Types</p>
                  <div className="flex flex-wrap gap-1.5">
                    {(u.event_types || []).map((e) => (
                      <span key={e} className="text-[10px] font-black uppercase bg-brand-blue text-white px-2 py-0.5 rounded">
                        {EVENT_TYPE_LABELS[e] || e}
                      </span>
                    ))}
                    {u.event_type_other && <span className="text-[10px] font-black uppercase bg-brand-blue text-white px-2 py-0.5 rounded">{u.event_type_other}</span>}
                    {(u.event_types || []).length === 0 && <span className="text-slate-400 text-[10px]">none</span>}
                  </div>
                </div>
                <div className="sm:col-span-2">
                  <p className="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-1">Equipment Wishlist</p>
                  <div className="flex flex-wrap gap-1.5">
                    {(u.equipment_wishlist || []).map((w) => (
                      <span key={w} className="text-[10px] font-black bg-brand-gold text-brand-blue px-2 py-0.5 rounded">{w}</span>
                    ))}
                    {(u.equipment_wishlist || []).length === 0 && <span className="text-slate-400 text-[10px]">none</span>}
                  </div>
                </div>
              </div>

              <div>
                <p className="text-[10px] font-black uppercase tracking-[0.3em] text-brand-red mb-2">
                  Booking History ({data.bookings?.length || 0})
                </p>
                {data.bookings?.length ? (
                  <div className="space-y-1.5 max-h-60 overflow-y-auto">
                    {data.bookings.map((b) => (
                      <div key={b.id} className="bg-white border border-slate-100 rounded-xl px-3 py-2 flex items-center justify-between text-xs" data-testid={`crm-booking-${b.id}`}>
                        <div>
                          <p className="font-black text-brand-blue tracking-tightest">#{b.id.slice(0, 8).toUpperCase()}</p>
                          <p className="text-[10px] font-bold text-slate-500">
                            {new Date(b.start_date).toLocaleDateString()} · {b.items?.reduce((s, i) => s + i.quantity, 0)} pcs · {b.status}
                          </p>
                        </div>
                        <p className="font-black text-brand-red">${(b.total || 0).toFixed(0)}</p>
                      </div>
                    ))}
                  </div>
                ) : <p className="text-slate-400 text-[11px] font-bold">No bookings yet.</p>}
              </div>

              <div>
                <p className="text-[10px] font-black uppercase tracking-[0.3em] text-brand-red mb-2">
                  Survey Responses ({data.surveys?.length || 0})
                </p>
                {data.surveys?.length ? (
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {data.surveys.map((s) => (
                      <div key={s.id} className="bg-white border border-slate-100 rounded-xl px-3 py-2.5" data-testid={`crm-survey-${s.id}`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-1.5">
                            {Array.from({ length: 5 }, (_, i) => (
                              <i key={i} className={`fas fa-star text-[11px] ${i < s.rating ? "text-brand-gold" : "text-slate-200"}`} />
                            ))}
                            {s.nps != null && <span className="text-[10px] font-bold text-slate-500 ml-2">NPS {s.nps}</span>}
                          </div>
                          <span className="text-[10px] font-bold text-slate-400">{new Date(s.created_at).toLocaleDateString()}</span>
                        </div>
                        {s.comment && <p className="text-[12px] text-slate-700 italic mt-1.5">"{s.comment}"</p>}
                        {s.equipment_wishlist?.length > 0 && (
                          <p className="text-[10px] font-bold text-slate-500 mt-1">
                            Wants: {s.equipment_wishlist.join(", ")}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                ) : <p className="text-slate-400 text-[11px] font-bold">No surveys yet.</p>}
              </div>

              <div className="flex gap-2 pt-2 border-t border-slate-100">
                {u.is_blocked ? (
                  <button onClick={() => block(false)} className="btn-primary px-5 py-2.5 rounded-xl font-black uppercase text-[10px] tracking-widest" data-testid="crm-unblock-button">
                    <i className="fas fa-circle-check mr-1.5" /> Unblock
                  </button>
                ) : (
                  <button onClick={() => block(true)} className="bg-brand-red text-white px-5 py-2.5 rounded-xl font-black uppercase text-[10px] tracking-widest hover:bg-red-700" data-testid="crm-block-button">
                    <i className="fas fa-ban mr-1.5" /> Block Customer
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <p className="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-0.5">{label}</p>
      <p className="text-xs font-bold text-brand-blue">{value}</p>
    </div>
  );
}



const LANDING_TEMPLATES = [
  { id: "graduation-2026", label: "Graduation Pack 2026", icon: "fa-graduation-cap" },
  { id: "wedding-backyard", label: "Backyard Wedding", icon: "fa-ring" },
  { id: "bbq-block-party", label: "BBQ Block Party", icon: "fa-fire-flame-curved" },
];

function CampaignsSection() {
  const [items, setItems] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState(false);

  const refresh = () => {
    setBusy(true);
    api.get("/admin/campaigns").then(({ data }) => setItems(data)).finally(() => setBusy(false));
  };
  useEffect(() => { refresh(); }, []);

  return (
    <section className="space-y-6" data-testid="admin-campaigns-section">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-3">
        <div>
          <p className="text-[10px] font-black uppercase tracking-[0.4em] text-brand-red mb-1.5">
            Performance Marketing
          </p>
          <h2 className="text-3xl font-black uppercase tracking-tightest text-brand-blue">
            Digital Campaigns
          </h2>
          <p className="text-slate-500 font-bold text-sm mt-1.5 max-w-3xl">
            Build a campaign once. AI generates ad copy + a master image, sized for Google,
            Meta, IG, and YouTube. Pair with a landing page to capture leads.
          </p>
        </div>
        <button onClick={() => setCreating(true)} className="btn-primary px-5 py-2.5 rounded-xl font-black uppercase text-[10px] tracking-widest self-start" data-testid="campaign-new-button">
          <i className="fas fa-plus mr-2" /> New Campaign
        </button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <a href="/lp/graduation-2026" target="_blank" rel="noreferrer" className="bg-brand-blue text-white rounded-[24px] p-5 hover:bg-brand-blue/90 transition-colors" data-testid="lp-shortcut-graduation">
          <i className="fas fa-graduation-cap text-2xl text-brand-gold" />
          <p className="text-[9px] font-black uppercase tracking-[0.3em] text-brand-gold mt-3">Live Landing Page</p>
          <p className="font-black text-sm uppercase mt-1.5 tracking-tightest">Graduation Pack 2026</p>
          <p className="text-[11px] text-white/70 mt-1">/lp/graduation-2026</p>
        </a>
        <a href="/lp/wedding-backyard" target="_blank" rel="noreferrer" className="bg-brand-blue text-white rounded-[24px] p-5 hover:bg-brand-blue/90 transition-colors" data-testid="lp-shortcut-wedding">
          <i className="fas fa-ring text-2xl text-brand-gold" />
          <p className="text-[9px] font-black uppercase tracking-[0.3em] text-brand-gold mt-3">Live Landing Page</p>
          <p className="font-black text-sm uppercase mt-1.5 tracking-tightest">Backyard Wedding</p>
          <p className="text-[11px] text-white/70 mt-1">/lp/wedding-backyard</p>
        </a>
        <a href="/lp/bbq-block-party" target="_blank" rel="noreferrer" className="bg-brand-blue text-white rounded-[24px] p-5 hover:bg-brand-blue/90 transition-colors" data-testid="lp-shortcut-bbq">
          <i className="fas fa-fire-flame-curved text-2xl text-brand-gold" />
          <p className="text-[9px] font-black uppercase tracking-[0.3em] text-brand-gold mt-3">Live Landing Page</p>
          <p className="font-black text-sm uppercase mt-1.5 tracking-tightest">BBQ Block Party</p>
          <p className="text-[11px] text-white/70 mt-1">/lp/bbq-block-party</p>
        </a>
      </div>

      <div className="space-y-2">
        <p className="text-[10px] font-black uppercase tracking-[0.3em] text-brand-red">Your Campaigns</p>
        {busy && <p className="text-slate-400 text-sm">Loading…</p>}
        {!busy && items.length === 0 && (
          <div className="bg-white rounded-[24px] border border-dashed border-slate-200 px-6 py-10 text-center">
            <i className="fas fa-rocket text-3xl text-slate-300 mb-2" />
            <p className="font-black uppercase tracking-tightest text-brand-blue text-sm">No campaigns yet</p>
            <p className="text-xs text-slate-500 font-bold mt-1">Click "New Campaign" to start.</p>
          </div>
        )}
        {items.map((c) => (
          <div key={c.id} onClick={() => setOpenId(c.id)} className="bg-white rounded-2xl border border-slate-100 p-4 hover:border-brand-blue cursor-pointer flex items-center gap-4" data-testid={`campaign-row-${c.id}`}>
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-black text-xs ${
              c.status === "live" ? "bg-emerald-100 text-emerald-700"
              : c.status === "paused" ? "bg-amber-100 text-amber-700"
              : "bg-slate-100 text-slate-500"
            }`}>{c.status?.charAt(0).toUpperCase() || "D"}</div>
            <div className="flex-1 min-w-0">
              <p className="font-black text-sm uppercase text-brand-blue tracking-tightest truncate">{c.name}</p>
              <p className="text-[11px] font-bold text-slate-500 mt-0.5">
                {c.target_event} · {c.landing_slug ? `→ /lp/${c.landing_slug}` : "no LP"} · {c.status}
              </p>
            </div>
            <div className="flex gap-1">
              {c.copy && <span className="text-[9px] font-black uppercase bg-brand-gold text-brand-blue px-2 py-0.5 rounded">Copy</span>}
              {c.image_path && <span className="text-[9px] font-black uppercase bg-brand-blue text-white px-2 py-0.5 rounded">Image</span>}
            </div>
          </div>
        ))}
      </div>

      <CampaignDialog cid={openId} creating={creating} onClose={() => { setOpenId(null); setCreating(false); }} onSaved={() => { refresh(); setOpenId(null); setCreating(false); }} />
    </section>
  );
}

function CampaignDialog({ cid, creating, onClose, onSaved }) {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ name: "", target_event: "", offer: "", audience_hint: "", landing_slug: "graduation-2026", status: "draft" });
  const [busy, setBusy] = useState(false);
  const [genBusy, setGenBusy] = useState({ copy: false, image: false });
  const [variants, setVariants] = useState([]);  // [{key,label,width,height,url}]

  const loadVariants = async (id) => {
    try {
      const fmtRes = await api.get(`/admin/campaigns/${id}/image-formats`);
      const formats = fmtRes.data.formats || [];
      const loaded = await Promise.all(
        formats.filter((f) => f.available).map(async (f) => {
          const r = await api.get(`/admin/campaigns/${id}/image?format=${f.key}`, { responseType: "blob" });
          return { ...f, url: URL.createObjectURL(r.data) };
        })
      );
      setVariants(loaded);
    } catch { setVariants([]); }
  };

  useEffect(() => {
    if (creating) {
      setData(null);
      setForm({ name: "", target_event: "", offer: "", audience_hint: "", landing_slug: "graduation-2026", status: "draft" });
      setVariants([]);
    } else if (cid) {
      api.get(`/admin/campaigns/${cid}`).then(({ data }) => {
        setData(data);
        setForm({ name: data.name, target_event: data.target_event, offer: data.offer, audience_hint: data.audience_hint || "", landing_slug: data.landing_slug || "", status: data.status });
        if (data.image_path || data.image_paths) loadVariants(cid);
        else setVariants([]);
      });
    }
  }, [cid, creating]);

  const save = async () => {
    setBusy(true);
    try {
      if (creating) { await api.post("/admin/campaigns", form); toast.success("Campaign created"); }
      else { await api.patch(`/admin/campaigns/${cid}`, form); toast.success("Saved"); }
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed"); }
    finally { setBusy(false); }
  };

  const generateCopy = async () => {
    if (!cid) { toast.error("Save campaign first"); return; }
    setGenBusy({ ...genBusy, copy: true });
    try {
      await api.post(`/admin/campaigns/${cid}/generate-copy`);
      const fresh = await api.get(`/admin/campaigns/${cid}`);
      setData(fresh.data);
      toast.success("AI copy generated — review & edit below");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Generation failed"); }
    finally { setGenBusy({ ...genBusy, copy: false }); }
  };

  const generateImage = async () => {
    if (!cid) { toast.error("Save campaign first"); return; }
    setGenBusy({ ...genBusy, image: true });
    try {
      await api.post(`/admin/campaigns/${cid}/generate-image`);
      await loadVariants(cid);
      toast.success("Master + 5 ad-format variants generated");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Image failed"); }
    finally { setGenBusy({ ...genBusy, image: false }); }
  };

  if (!cid && !creating) return null;
  const c = data;
  const copyBlob = c?.copy && !c.copy.parse_error ? c.copy : null;

  return (
    <Dialog open={!!cid || creating} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl bg-white rounded-[28px] max-h-[90vh] overflow-y-auto" data-testid="campaign-dialog">
        <DialogHeader>
          <DialogTitle className="text-brand-blue font-black uppercase tracking-tightest text-2xl">
            {creating ? "New Campaign" : `Edit · ${c?.name || ""}`}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          <div className="grid sm:grid-cols-2 gap-3">
            <Input label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testId="cmp-name" />
            <Input label="Target Event" value={form.target_event} onChange={(v) => setForm({ ...form, target_event: v })} testId="cmp-event" />
          </div>
          <Input label="Offer / Hook" value={form.offer} onChange={(v) => setForm({ ...form, offer: v })} testId="cmp-offer" />
          <Input label="Audience Hint" value={form.audience_hint} onChange={(v) => setForm({ ...form, audience_hint: v })} testId="cmp-audience" />
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] font-black uppercase tracking-widest text-brand-blue mb-1">Landing Page</label>
              <select value={form.landing_slug} onChange={(e) => setForm({ ...form, landing_slug: e.target.value })} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm font-bold" data-testid="cmp-landing">
                <option value="">— None —</option>
                {LANDING_TEMPLATES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[10px] font-black uppercase tracking-widest text-brand-blue mb-1">Status</label>
              <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm font-bold" data-testid="cmp-status">
                <option value="draft">Draft</option>
                <option value="live">Live</option>
                <option value="paused">Paused</option>
              </select>
            </div>
          </div>
          <button onClick={save} disabled={busy || !form.name} className="btn-primary px-5 py-2.5 rounded-xl font-black uppercase text-[10px] tracking-widest disabled:opacity-50" data-testid="cmp-save-button">
            {busy ? <><i className="fas fa-spinner fa-spin mr-2" /> Saving</> : <><i className="fas fa-save mr-2" /> {creating ? "Create" : "Save"}</>}
          </button>

          {!creating && (
            <>
              <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                <p className="text-[10px] font-black uppercase tracking-[0.3em] text-brand-red">AI Asset Generator</p>
                <div className="flex gap-2">
                  <button onClick={generateCopy} disabled={genBusy.copy} className="btn-primary px-4 py-2 rounded-lg font-black uppercase text-[10px] tracking-widest disabled:opacity-50" data-testid="cmp-gen-copy">
                    {genBusy.copy ? <><i className="fas fa-spinner fa-spin mr-2" /></> : <><i className="fas fa-pen-to-square mr-2" /> Generate Copy</>}
                  </button>
                  <button onClick={generateImage} disabled={genBusy.image || !c?.image_prompt} title={!c?.image_prompt ? "Generate copy first to get an image prompt" : ""} className="bg-brand-gold text-brand-blue px-4 py-2 rounded-lg font-black uppercase text-[10px] tracking-widest disabled:opacity-50 hover:bg-amber-300 transition-colors" data-testid="cmp-gen-image">
                    {genBusy.image ? <><i className="fas fa-spinner fa-spin mr-2" /></> : <><i className="fas fa-image mr-2" /> Generate Images (All Formats)</>}
                  </button>
                </div>
              </div>

              {variants.length > 0 && (
                <div className="bg-slate-50 rounded-2xl p-4 space-y-3" data-testid="cmp-image-preview">
                  <div className="flex items-center justify-between">
                    <p className="text-[10px] font-black uppercase tracking-[0.3em] text-brand-red">AI Ad Assets — {variants.length} Format{variants.length === 1 ? "" : "s"}</p>
                    <span className="text-[9px] font-black uppercase tracking-widest text-slate-500">Click to download</span>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {variants.map((v) => (
                      <a
                        key={v.key}
                        href={v.url}
                        download={`campaign-${cid}-${v.key}.png`}
                        className="block bg-white rounded-xl border border-slate-200 overflow-hidden hover:border-brand-gold transition-colors group"
                        data-testid={`cmp-variant-${v.key}`}
                      >
                        <div className="bg-slate-100 flex items-center justify-center" style={{ aspectRatio: `${v.width}/${v.height}`, maxHeight: 220 }}>
                          <img src={v.url} alt={v.label} className="w-full h-full object-contain" />
                        </div>
                        <div className="p-2">
                          <p className="text-[9px] font-black uppercase tracking-widest text-brand-blue truncate">{v.label}</p>
                          <p className="text-[9px] text-slate-500 font-bold mt-0.5">{v.width}×{v.height} <i className="fas fa-download ml-1 group-hover:text-brand-red" /></p>
                        </div>
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {copyBlob && <CopyPanel copy={copyBlob} cid={cid} onSaved={onSaved} />}
              {c?.copy?.parse_error && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-[11px] font-bold text-amber-800">
                  AI returned non-JSON. Raw output: <pre className="mt-2 whitespace-pre-wrap text-[10px]">{c.copy.raw}</pre>
                </div>
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CopyPanel({ copy, cid, onSaved }) {
  const [edited, setEdited] = useState(JSON.stringify(copy, null, 2));
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const parsed = JSON.parse(edited);
      await api.patch(`/admin/campaigns/${cid}`, { copy: parsed });
      toast.success("Copy saved");
      onSaved?.();
    } catch (e) { toast.error("Invalid JSON — fix syntax first"); }
    finally { setBusy(false); }
  };

  const sections = [
    { id: "google_search", label: "Google Search Ad" },
    { id: "meta_feed", label: "Meta Feed" },
    { id: "meta_story", label: "Meta / IG Story / Reels" },
    { id: "instagram_carousel", label: "IG Carousel (5 slides)" },
    { id: "youtube_short", label: "YouTube Short" },
  ];

  return (
    <div className="space-y-3" data-testid="cmp-copy-panel">
      {sections.map((s) => (
        <div key={s.id} className="bg-white border border-slate-100 rounded-xl p-3.5">
          <p className="text-[10px] font-black uppercase tracking-[0.3em] text-brand-red mb-2">{s.label}</p>
          <pre className="text-[11px] text-slate-700 whitespace-pre-wrap font-mono">
            {JSON.stringify(copy[s.id] || {}, null, 2)}
          </pre>
        </div>
      ))}
      <details className="bg-slate-50 rounded-xl p-3">
        <summary className="text-[10px] font-black uppercase tracking-widest text-brand-blue cursor-pointer">
          Edit raw JSON
        </summary>
        <textarea value={edited} onChange={(e) => setEdited(e.target.value)} rows={12} className="mt-2 w-full font-mono text-[11px] rounded-lg border border-slate-200 px-3 py-2" data-testid="cmp-copy-json" />
        <button onClick={save} disabled={busy} className="mt-2 btn-primary px-4 py-2 rounded-lg font-black uppercase text-[10px] tracking-widest disabled:opacity-50" data-testid="cmp-copy-save">
          {busy ? "Saving…" : "Save Edits"}
        </button>
      </details>
    </div>
  );
}


function FieldMarketingSection() {
  const [drops, setDrops] = useState([]);
  const [byZip, setByZip] = useState({});
  const [totalFlyers, setTotalFlyers] = useState(0);
  const [spend, setSpend] = useState([]);
  const [byChannel, setByChannel] = useState({});
  const [dashboard, setDashboard] = useState(null);
  const [spendForm, setSpendForm] = useState({ week_start: thisMonday(), channel: "google_ads", spend: 0, impressions: 0, clicks: 0, conversions: 0 });
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    const [d, s, dash] = await Promise.all([
      api.get("/admin/flyer-drops"),
      api.get("/admin/ad-spend"),
      api.get("/admin/marketing/dashboard"),
    ]);
    setDrops(d.data.items || []);
    setByZip(d.data.by_zip || {});
    setTotalFlyers(d.data.total_flyers || 0);
    setSpend(s.data.items || []);
    setByChannel(s.data.by_channel || {});
    setDashboard(dash.data);
  };
  useEffect(() => { refresh(); }, []);

  const saveSpend = async () => {
    setBusy(true);
    try {
      await api.post("/admin/ad-spend", { ...spendForm, spend: parseFloat(spendForm.spend) || 0, impressions: parseInt(spendForm.impressions) || 0, clicks: parseInt(spendForm.clicks) || 0, conversions: parseInt(spendForm.conversions) || 0 });
      toast.success("Ad spend logged");
      refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <section className="space-y-6" data-testid="admin-field-section">
      <div>
        <p className="text-[10px] font-black uppercase tracking-[0.4em] text-brand-red mb-1.5">Brand Awareness Sheet</p>
        <h2 className="text-3xl font-black uppercase tracking-tightest text-brand-blue">Field Marketing</h2>
        <p className="text-slate-500 font-bold text-sm mt-1.5 max-w-3xl">
          Flyer drops, weekly ad-spend ledger, and the rollup that ties it all to leads.
        </p>
      </div>

      {dashboard && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Stat label="Total Leads" value={dashboard.leads_total} icon="fa-magnet" tone="emerald" />
          <Stat label="Leads (30d)" value={dashboard.leads_30d} icon="fa-bolt" tone="amber" />
          <Stat label="Ad Spend" value={`$${dashboard.ad_spend_total.toFixed(0)}`} icon="fa-dollar-sign" tone="blue" />
          <Stat label="Cost / Lead" value={dashboard.cost_per_lead != null ? `$${dashboard.cost_per_lead.toFixed(2)}` : "—"} icon="fa-coins" />
          <Stat label="Flyers Dropped" value={dashboard.flyer_drops_total} icon="fa-newspaper" />
          <Stat label="Newsletter Subs" value={dashboard.newsletter_subscribers} icon="fa-envelope" />
          <Stat label="Avg Rating" value={dashboard.avg_survey_rating ?? "—"} icon="fa-star" tone="amber" />
          <Stat label="Avg NPS" value={dashboard.avg_nps ?? "—"} icon="fa-thumbs-up" tone="emerald" />
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-[24px] border border-slate-100 p-5">
          <div className="flex items-end justify-between">
            <div>
              <p className="text-[10px] font-black uppercase tracking-[0.3em] text-brand-red">Flyer Drops</p>
              <p className="text-2xl font-black uppercase text-brand-blue tracking-tightest">{totalFlyers.toLocaleString()} dropped</p>
            </div>
            <a href="/admin/flyer-drop" target="_blank" rel="noreferrer" className="btn-primary px-4 py-2 rounded-lg font-black uppercase text-[10px] tracking-widest" data-testid="field-mobile-link">
              <i className="fas fa-mobile-alt mr-1.5" /> Mobile Logger
            </a>
          </div>
          <div className="mt-4 space-y-1.5 max-h-72 overflow-y-auto">
            {Object.entries(byZip).sort((a, b) => b[1] - a[1]).map(([z, count]) => (
              <div key={z} className="flex items-center justify-between bg-slate-50 rounded-lg px-3 py-2">
                <span className="font-black text-xs text-brand-blue tracking-tightest">{z}</span>
                <span className="font-black text-xs text-brand-red">{count}</span>
              </div>
            ))}
            {drops.length === 0 && <p className="text-slate-400 text-xs font-bold text-center py-6">No drops logged yet — open the mobile logger.</p>}
          </div>
        </div>

        <div className="bg-white rounded-[24px] border border-slate-100 p-5">
          <p className="text-[10px] font-black uppercase tracking-[0.3em] text-brand-red mb-2">Ad Spend (Weekly)</p>
          <div className="grid grid-cols-2 gap-2 mb-3">
            <FormField label="Week Start" type="date" value={spendForm.week_start} onChange={(v) => setSpendForm({ ...spendForm, week_start: v })} testId="spend-week" />
            <div>
              <label className="block text-[10px] font-black uppercase tracking-widest text-brand-blue mb-1">Channel</label>
              <select value={spendForm.channel} onChange={(e) => setSpendForm({ ...spendForm, channel: e.target.value })} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm font-bold" data-testid="spend-channel">
                <option value="google_ads">Google Ads</option>
                <option value="meta_ads">Meta Ads</option>
                <option value="youtube">YouTube</option>
                <option value="other">Other</option>
              </select>
            </div>
            <FormField label="Spend ($)" type="number" value={spendForm.spend} onChange={(v) => setSpendForm({ ...spendForm, spend: v })} testId="spend-amount" />
            <FormField label="Clicks" type="number" value={spendForm.clicks} onChange={(v) => setSpendForm({ ...spendForm, clicks: v })} testId="spend-clicks" />
            <FormField label="Impressions" type="number" value={spendForm.impressions} onChange={(v) => setSpendForm({ ...spendForm, impressions: v })} testId="spend-impressions" />
            <FormField label="Conversions" type="number" value={spendForm.conversions} onChange={(v) => setSpendForm({ ...spendForm, conversions: v })} testId="spend-conversions" />
          </div>
          <button onClick={saveSpend} disabled={busy} className="btn-primary w-full py-2.5 rounded-xl font-black uppercase text-[10px] tracking-widest disabled:opacity-50" data-testid="spend-save">
            {busy ? "Saving…" : "Log This Week's Spend"}
          </button>
          <div className="mt-4 space-y-1.5 max-h-48 overflow-y-auto">
            {Object.entries(byChannel).map(([ch, agg]) => (
              <div key={ch} className="flex items-center justify-between bg-slate-50 rounded-lg px-3 py-2 text-xs font-bold">
                <span className="text-brand-blue uppercase">{ch.replace("_", " ")}</span>
                <span>${agg.spend.toFixed(0)} · {agg.clicks} clicks · {agg.conversions} conv</span>
              </div>
            ))}
            {spend.length === 0 && <p className="text-slate-400 text-xs font-bold text-center py-3">No ad spend logged yet.</p>}
          </div>
        </div>
      </div>

      <div className="bg-brand-blue text-white rounded-[24px] p-5" data-testid="field-google-ads-stub">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-2xl bg-brand-gold text-brand-blue flex items-center justify-center flex-shrink-0">
            <i className="fab fa-google text-xl" />
          </div>
          <div className="flex-1">
            <p className="text-[10px] font-black uppercase tracking-[0.3em] text-brand-gold">Google Ads API · Connection Pending</p>
            <p className="text-base font-black uppercase tracking-tightest mt-1">Auto-pull spend & conversions</p>
            <p className="text-[11px] text-white/80 font-medium mt-1.5 leading-snug">
              Manual entry works today. To auto-sync, we need: (1) your Google Ads <strong>Customer ID</strong>,
              (2) a <strong>Developer Token</strong> from Google Ads API Center (1–3 day approval),
              and (3) OAuth consent. Send those when you have them and I'll wire the sync.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function thisMonday() {
  const d = new Date();
  const day = d.getDay();
  const diff = (day === 0 ? -6 : 1) - day;
  d.setDate(d.getDate() + diff);
  return d.toISOString().slice(0, 10);
}

function FormField({ label, value, onChange, type = "text", testId }) {
  return (
    <div>
      <label className="block text-[10px] font-black uppercase tracking-widest text-brand-blue mb-1">{label}</label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm font-bold focus:outline-none focus:border-brand-blue" data-testid={testId} />
    </div>
  );
}


// =====================================================================
// Access Codes — manual eSmartLock workflow (bridge until Cloud SDK)
// =====================================================================
const STAGE_META = {
  needs_codes:    { label: "Needs Codes",     color: "bg-red-100 text-red-800 border-red-300",         accent: "border-red-500" },
  codes_sent:     { label: "Codes Sent",      color: "bg-amber-100 text-amber-900 border-amber-300",   accent: "border-amber-500" },
  awaiting_lock:  { label: "Awaiting Lock",   color: "bg-blue-100 text-blue-800 border-blue-300",      accent: "border-blue-500" },
  lock_confirmed: { label: "Lock Confirmed",  color: "bg-emerald-100 text-emerald-800 border-emerald-300", accent: "border-emerald-500" },
  codes_deleted:  { label: "Codes Deleted",   color: "bg-slate-100 text-slate-600 border-slate-300",   accent: "border-slate-400" },
};

function AccessCodesSection() {
  const [data, setData] = useState({ stages: [], counts: {}, buckets: {} });
  const [selected, setSelected] = useState(null);  // booking obj
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/access-codes/kanban");
      setData(data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, []);

  const total = Object.values(data.counts || {}).reduce((a, b) => a + b, 0);
  const openTasks = (data.counts?.needs_codes || 0) + (data.counts?.lock_confirmed || 0);

  return (
    <section className="space-y-8" data-testid="access-codes-section">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <p className="text-[10px] font-black uppercase tracking-[0.3em] text-brand-red">Pod Access</p>
          <h2 className="text-3xl md:text-4xl font-black uppercase tracking-tightest text-brand-blue">Access Codes</h2>
          <p className="text-slate-500 text-sm font-bold mt-2">
            Manually program pickup + dropoff codes on the lock, enter them here, and the system delivers them to the customer with pod rules.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {openTasks > 0 && (
            <span className="bg-brand-red text-white text-[10px] font-black uppercase tracking-widest px-3 py-1.5 rounded-full" data-testid="ac-tasks-badge">
              <i className="fas fa-triangle-exclamation mr-1.5" /> {openTasks} task{openTasks > 1 ? "s" : ""}
            </span>
          )}
          <button onClick={refresh} className="text-[10px] font-black uppercase tracking-widest text-brand-blue hover:text-brand-red transition-colors" data-testid="ac-refresh-btn">
            <i className="fas fa-arrows-rotate mr-1.5" /> Refresh
          </button>
        </div>
      </header>

      {loading ? (
        <div className="text-center py-16 text-slate-400 font-black uppercase text-xs tracking-widest">
          <i className="fas fa-spinner fa-spin mr-2" /> Loading kanban…
        </div>
      ) : total === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-slate-100" data-testid="ac-empty">
          <i className="fas fa-key text-5xl text-slate-200 mb-4" />
          <p className="text-slate-500 font-bold">No paid bookings yet. Codes will appear here once orders come in.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-5 gap-4">
          {(data.stages || []).map((stage) => (
            <KanbanColumn
              key={stage}
              stage={stage}
              bookings={data.buckets?.[stage] || []}
              onOpen={setSelected}
            />
          ))}
        </div>
      )}

      {selected && (
        <AccessCodesDialog
          booking={selected}
          onClose={() => setSelected(null)}
          onSaved={() => { setSelected(null); refresh(); }}
        />
      )}
    </section>
  );
}

function KanbanColumn({ stage, bookings, onOpen }) {
  const meta = STAGE_META[stage] || { label: stage, color: "bg-slate-100", accent: "border-slate-300" };
  return (
    <div className={`bg-slate-50 rounded-2xl border-t-4 ${meta.accent} p-3 min-h-[160px]`} data-testid={`ac-col-${stage}`}>
      <div className="flex items-center justify-between mb-3 px-1">
        <p className="text-[10px] font-black uppercase tracking-widest text-brand-blue">{meta.label}</p>
        <span className="text-[10px] font-black bg-white border border-slate-200 px-2 py-0.5 rounded-full text-slate-500">{bookings.length}</span>
      </div>
      <div className="space-y-2">
        {bookings.length === 0 && (
          <p className="text-[10px] text-slate-400 font-bold text-center py-6">—</p>
        )}
        {bookings.map((b) => (
          <button
            key={b.id}
            onClick={() => onOpen(b)}
            className="w-full text-left bg-white rounded-xl border border-slate-100 hover:border-brand-gold hover:shadow-md transition-all p-3"
            data-testid={`ac-card-${b.id}`}
          >
            <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1">#{b.id.slice(0,8)}</p>
            <p className="font-black text-brand-blue text-sm uppercase tracking-tight leading-tight mb-1">{b.customer_name || b.user_email}</p>
            <p className="text-[10px] text-slate-500 font-bold">{fmtDate(b.start_date)} → {fmtDate(b.end_date)}</p>
            {b.pickup_code && b.dropoff_code && (
              <div className="mt-2 flex gap-1.5">
                <span className="text-[9px] font-mono font-black bg-brand-blue text-white px-2 py-0.5 rounded" title="Pickup code">P: {b.pickup_code}</span>
                <span className="text-[9px] font-mono font-black bg-brand-red text-white px-2 py-0.5 rounded" title="Dropoff code">D: {b.dropoff_code}</span>
              </div>
            )}
            {b.has_lock_photo && (
              <p className="mt-2 text-[9px] font-black uppercase tracking-widest text-emerald-600"><i className="fas fa-camera mr-1" /> Photo received</p>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });
  } catch { return iso; }
}

function toLocalDatetimeInput(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch { return ""; }
}

function AccessCodesDialog({ booking, onClose, onSaved }) {
  const stage = (() => {
    if (booking.codes_deleted_at) return "codes_deleted";
    if (booking.lock_confirmation_at) return "lock_confirmed";
    if (booking.access_codes_sent_at) return "codes_sent";
    return "needs_codes";
  })();

  const [podHasIgloo, setPodHasIgloo] = useState(null); // true/false/null=loading
  useEffect(() => {
    api.get("/pods/park-hill").then(({ data }) => setPodHasIgloo(!!data.igloo_device_id)).catch(() => setPodHasIgloo(false));
  }, []);

  // Default windows: pickup = 1h before start_date → end of that day; dropoff = 1h before end_date → 2h after.
  const defaultPickupStart = booking.pickup_window_start || (booking.start_date ? new Date(new Date(booking.start_date).getTime() - 60*60*1000).toISOString() : "");
  const defaultPickupEnd = booking.pickup_window_end || (booking.start_date ? new Date(new Date(booking.start_date).getTime() + 12*60*60*1000).toISOString() : "");
  const defaultDropoffStart = booking.dropoff_window_start || (booking.end_date ? new Date(new Date(booking.end_date).getTime() - 60*60*1000).toISOString() : "");
  const defaultDropoffEnd = booking.dropoff_window_end || (booking.end_date ? new Date(new Date(booking.end_date).getTime() + 2*60*60*1000).toISOString() : "");

  const [pickupCode, setPickupCode] = useState(booking.pickup_code || "");
  const [dropoffCode, setDropoffCode] = useState(booking.dropoff_code || "");
  const [pickupStart, setPickupStart] = useState(toLocalDatetimeInput(defaultPickupStart));
  const [pickupEnd, setPickupEnd] = useState(toLocalDatetimeInput(defaultPickupEnd));
  const [dropoffStart, setDropoffStart] = useState(toLocalDatetimeInput(defaultDropoffStart));
  const [dropoffEnd, setDropoffEnd] = useState(toLocalDatetimeInput(defaultDropoffEnd));
  const [notes, setNotes] = useState(booking.access_notes || "");
  const [busy, setBusy] = useState(false);
  const [photoUrl, setPhotoUrl] = useState(null);

  useEffect(() => {
    if (booking.has_lock_photo) {
      api.get(`/admin/bookings/${booking.id}/lock-photo`, { responseType: "blob" })
        .then((r) => setPhotoUrl(URL.createObjectURL(r.data)))
        .catch(() => {});
    }
  }, [booking.id, booking.has_lock_photo]);

  const submit = async () => {
    if (!pickupCode || !dropoffCode || !pickupStart || !pickupEnd || !dropoffStart || !dropoffEnd) {
      toast.error("All code + window fields are required");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/bookings/${booking.id}/access-codes`, {
        pickup_code: pickupCode,
        dropoff_code: dropoffCode,
        pickup_window_start: new Date(pickupStart).toISOString(),
        pickup_window_end: new Date(pickupEnd).toISOString(),
        dropoff_window_start: new Date(dropoffStart).toISOString(),
        dropoff_window_end: new Date(dropoffEnd).toISOString(),
        notes: notes || null,
      });
      const emailOK = data.email?.sent;
      const smsOK = data.sms?.sent;
      toast.success(`Codes saved • Email: ${emailOK ? "✅" : "—"} · SMS: ${smsOK ? "✅" : "—"}`);
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally { setBusy(false); }
  };

  const autoGenerate = async () => {
    if (!pickupStart || !pickupEnd || !dropoffStart || !dropoffEnd) {
      toast.error("Set both time windows first");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/bookings/${booking.id}/access-codes/auto`, {
        pickup_window_start: new Date(pickupStart).toISOString(),
        pickup_window_end: new Date(pickupEnd).toISOString(),
        dropoff_window_start: new Date(dropoffStart).toISOString(),
        dropoff_window_end: new Date(dropoffEnd).toISOString(),
        notes: notes || null,
        pod_slug: "park-hill",
      });
      setPickupCode(data.pickup_code);
      setDropoffCode(data.dropoff_code);
      const emailOK = data.email?.sent;
      const smsOK = data.sms?.sent;
      toast.success(`Live PINs generated • Email: ${emailOK ? "✅" : "—"} · SMS: ${smsOK ? "✅" : "—"}`);
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "igloohome PIN generation failed");
    } finally { setBusy(false); }
  };

  const resend = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/bookings/${booking.id}/resend-codes`);
      toast.success("Codes resent to customer");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Resend failed");
    } finally { setBusy(false); }
  };

  const markDeleted = async () => {
    if (!confirm("Confirm you've deleted these codes from the physical lock?")) return;
    setBusy(true);
    try {
      await api.post(`/admin/bookings/${booking.id}/codes-deleted`);
      toast.success("Marked as codes-deleted. Great work!");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl bg-white rounded-[28px] max-h-[90vh] overflow-y-auto" data-testid="ac-dialog">
        <DialogHeader>
          <DialogTitle className="text-brand-blue font-black uppercase tracking-tightest text-2xl">
            Access Codes · {booking.customer_name || booking.user_email}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          <div className="flex flex-wrap items-center gap-2 -mt-2">
            <span className={`text-[9px] font-black uppercase tracking-widest px-2 py-1 rounded-full border ${STAGE_META[stage]?.color || ""}`}>
              {STAGE_META[stage]?.label || stage}
            </span>
            <span className="text-[10px] font-bold text-slate-500">Booking #{booking.id.slice(0,8)} · {fmtDate(booking.start_date)} → {fmtDate(booking.end_date)}</span>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] font-black uppercase tracking-widest text-brand-blue mb-1">Pickup Code</label>
              <input value={pickupCode} onChange={(e) => setPickupCode(e.target.value)} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm font-mono font-black focus:outline-none focus:border-brand-blue" placeholder="e.g. 4821" data-testid="ac-pickup-code" />
            </div>
            <div>
              <label className="block text-[10px] font-black uppercase tracking-widest text-brand-red mb-1">Dropoff Code</label>
              <input value={dropoffCode} onChange={(e) => setDropoffCode(e.target.value)} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm font-mono font-black focus:outline-none focus:border-brand-red" placeholder="e.g. 7365" data-testid="ac-dropoff-code" />
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] font-black uppercase tracking-widest text-brand-blue mb-1">Pickup Window Start</label>
              <input type="datetime-local" value={pickupStart} onChange={(e) => setPickupStart(e.target.value)} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm font-bold" data-testid="ac-pickup-start" />
            </div>
            <div>
              <label className="block text-[10px] font-black uppercase tracking-widest text-brand-blue mb-1">Pickup Window End</label>
              <input type="datetime-local" value={pickupEnd} onChange={(e) => setPickupEnd(e.target.value)} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm font-bold" data-testid="ac-pickup-end" />
            </div>
            <div>
              <label className="block text-[10px] font-black uppercase tracking-widest text-brand-red mb-1">Dropoff Window Start</label>
              <input type="datetime-local" value={dropoffStart} onChange={(e) => setDropoffStart(e.target.value)} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm font-bold" data-testid="ac-dropoff-start" />
            </div>
            <div>
              <label className="block text-[10px] font-black uppercase tracking-widest text-brand-red mb-1">Dropoff Window End</label>
              <input type="datetime-local" value={dropoffEnd} onChange={(e) => setDropoffEnd(e.target.value)} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm font-bold" data-testid="ac-dropoff-end" />
            </div>
          </div>

          <div>
            <label className="block text-[10px] font-black uppercase tracking-widest text-brand-blue mb-1">Custom Note (optional) — appears in the email</label>
            <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm font-bold focus:outline-none focus:border-brand-blue" placeholder="Anything specific for this customer? Special instructions?" data-testid="ac-notes" />
          </div>

          <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-100">
            {podHasIgloo && (
              <button onClick={autoGenerate} disabled={busy} className="bg-brand-blue text-brand-gold px-5 py-2.5 rounded-xl font-black uppercase text-[10px] tracking-widest hover:opacity-90 disabled:opacity-50" data-testid="ac-auto-btn" title="Generate secure algoPINs from your igloohome Keybox">
                {busy ? <><i className="fas fa-spinner fa-spin mr-2" /> Generating</> : <><i className="fas fa-wand-magic-sparkles mr-2" /> Generate Live PINs</>}
              </button>
            )}
            <button onClick={submit} disabled={busy} className="btn-primary px-5 py-2.5 rounded-xl font-black uppercase text-[10px] tracking-widest disabled:opacity-50" data-testid="ac-save-btn">
              {busy ? <><i className="fas fa-spinner fa-spin mr-2" /> Sending</> : <><i className="fas fa-paper-plane mr-2" /> Save & Send</>}
            </button>
            {booking.access_codes_sent_at && (
              <button onClick={resend} disabled={busy} className="bg-slate-100 text-brand-blue px-4 py-2.5 rounded-xl font-black uppercase text-[10px] tracking-widest hover:bg-slate-200 disabled:opacity-50" data-testid="ac-resend-btn">
                <i className="fas fa-arrows-rotate mr-2" /> Resend to Customer
              </button>
            )}
            {(stage === "lock_confirmed") && (
              <button onClick={markDeleted} disabled={busy} className="bg-emerald-500 text-white px-4 py-2.5 rounded-xl font-black uppercase text-[10px] tracking-widest hover:bg-emerald-600 disabled:opacity-50" data-testid="ac-deleted-btn">
                <i className="fas fa-check-double mr-2" /> I&apos;ve Deleted the Codes
              </button>
            )}
          </div>

          {photoUrl && (
            <div className="bg-slate-50 rounded-2xl p-4" data-testid="ac-photo-block">
              <p className="text-[10px] font-black uppercase tracking-widest text-emerald-700 mb-2"><i className="fas fa-camera mr-1.5" /> Lock-Closed Photo · Uploaded {fmtDate(booking.lock_confirmation_at)}</p>
              <img src={photoUrl} alt="Lock closed" className="rounded-xl w-full max-w-sm" />
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}



// =====================================================================
// Pod Locations — upload real photos or AI-generate a render per pod
// =====================================================================
function PodLocationsSection() {
  const [pods, setPods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busySlug, setBusySlug] = useState(null);
  const [iglooDevices, setIglooDevices] = useState(null);
  const [showMapperFor, setShowMapperFor] = useState(null);
  const [showAdd, setShowAdd] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/pods");
      setPods(data.items || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, []);

  const loadIglooDevices = async () => {
    setIglooDevices({ loading: true, list: [] });
    try {
      const { data } = await api.get("/admin/igloo/devices");
      setIglooDevices({ loading: false, list: data.devices || [] });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "igloohome unreachable");
      setIglooDevices({ loading: false, list: [], error: true });
    }
  };

  const mapDevice = async (slug, device) => {
    setBusySlug(slug);
    try {
      await api.post(`/admin/pods/${slug}/igloo-device`, {
        device_id: device.deviceId || device.id || device.device_id,
        device_name: device.deviceName || device.name || device.device_name || device.type,
      });
      toast.success("igloohome device linked");
      setShowMapperFor(null);
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Link failed");
    } finally { setBusySlug(null); }
  };

  const unmapDevice = async (slug) => {
    if (!confirm("Unlink the igloohome device from this pod?")) return;
    setBusySlug(slug);
    try {
      await api.delete(`/admin/pods/${slug}/igloo-device`);
      toast.success("Unlinked");
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally { setBusySlug(null); }
  };

  const resolvePhoto = (url) => {
    if (!url) return "";
    if (url.startsWith("/api")) return `${process.env.REACT_APP_BACKEND_URL}${url}`;
    return url;
  };

  const upload = async (slug, file) => {
    if (!file) return;
    setBusySlug(slug);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/admin/pods/${slug}/photo`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Photo uploaded");
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed");
    } finally { setBusySlug(null); }
  };

  const generate = async (slug) => {
    setBusySlug(slug);
    try {
      await api.post(`/admin/pods/${slug}/generate-photo`);
      toast.success("AI render generated");
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Render failed");
    } finally { setBusySlug(null); }
  };

  return (
    <section className="space-y-6" data-testid="pods-section">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <p className="text-slate-500 text-sm font-bold">
            Photos, mapping, and igloohome linking for every pod in your fleet.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowAdd(true)}
            className="text-[10px] font-black uppercase tracking-widest bg-brand-red text-white hover:opacity-90 px-4 py-2.5 rounded-xl transition-opacity"
            data-testid="pods-add-btn"
          >
            <i className="fas fa-plus mr-1.5" /> Add Pod
          </button>
          <button
            onClick={async () => {
              try {
                await api.post("/admin/pods/battery-check");
                toast.success("Battery check complete");
                refresh();
              } catch (e) {
                toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Battery check failed");
              }
            }}
            className="text-[10px] font-black uppercase tracking-widest bg-slate-100 hover:bg-brand-blue hover:text-white text-brand-blue px-4 py-2.5 rounded-xl transition-colors"
            data-testid="pods-battery-check-now"
          >
            <i className="fas fa-battery-half mr-1.5" /> Check Batteries Now
          </button>
        </div>
      </header>
      {showAdd && <AddPodDialog open={showAdd} onClose={() => setShowAdd(false)} onCreated={refresh} />}

      {loading ? (
        <div className="text-center py-16 text-slate-400 font-black uppercase text-xs tracking-widest">
          <i className="fas fa-spinner fa-spin mr-2" /> Loading pods…
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-6">
          {pods.map((pod) => (
            <div key={pod.id} className="bg-white rounded-[24px] border border-slate-100 overflow-hidden shadow-sm" data-testid={`pods-admin-card-${pod.slug}`}>
              <div className="relative h-56 bg-slate-100">
                <img
                  src={resolvePhoto(pod.photo_url)}
                  alt={pod.name}
                  className="w-full h-full object-cover"
                  onError={(e) => { e.currentTarget.style.opacity = "0.3"; }}
                />
                <div className="absolute top-3 left-3 flex gap-2">
                  {pod.status === "active" ? (
                    <span className="bg-emerald-500 text-white text-[9px] font-black uppercase tracking-widest px-2 py-1 rounded-full">Live</span>
                  ) : (
                    <span className="bg-brand-gold text-brand-blue text-[9px] font-black uppercase tracking-widest px-2 py-1 rounded-full">Opening Soon</span>
                  )}
                </div>
                {pod.igloo_device_id && (
                  <div className="absolute top-3 right-3">
                    <span className="bg-brand-blue text-brand-gold text-[9px] font-black uppercase tracking-widest px-2 py-1 rounded-full">
                      <i className="fas fa-lock mr-1" /> igloohome linked
                    </span>
                  </div>
                )}
              </div>
              <div className="p-5 space-y-3">
                <div>
                  <p className="text-[10px] font-black uppercase tracking-widest text-brand-red">{pod.neighborhood}</p>
                  <h3 className="text-xl font-black uppercase tracking-tight text-brand-blue">{pod.name}</h3>
                  <p className="text-xs text-slate-500 font-bold mt-1">{pod.address}</p>
                </div>

                {/* Photo controls */}
                <div className="flex flex-col sm:flex-row gap-2 pt-1">
                  <label className="flex-1 cursor-pointer bg-brand-blue text-white text-center py-2.5 rounded-xl font-black uppercase text-[10px] tracking-widest hover:bg-brand-blue/90 transition-colors" data-testid={`pods-upload-${pod.slug}`}>
                    <input type="file" accept="image/*" className="hidden" disabled={busySlug === pod.slug} onChange={(e) => upload(pod.slug, e.target.files?.[0])} />
                    {busySlug === pod.slug ? <><i className="fas fa-spinner fa-spin mr-1.5" /></> : <><i className="fas fa-upload mr-1.5" /> Upload Photo</>}
                  </label>
                  <button onClick={() => generate(pod.slug)} disabled={busySlug === pod.slug} className="flex-1 bg-brand-gold text-brand-blue py-2.5 rounded-xl font-black uppercase text-[10px] tracking-widest hover:bg-amber-300 transition-colors disabled:opacity-50" data-testid={`pods-generate-${pod.slug}`}>
                    {busySlug === pod.slug ? <><i className="fas fa-spinner fa-spin mr-1.5" /></> : <><i className="fas fa-wand-magic-sparkles mr-1.5" /> AI Render</>}
                  </button>
                </div>

                {/* igloohome linking */}
                <div className="border-t border-slate-100 pt-3">
                  {pod.igloo_device_id ? (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <p className="text-[10px] font-black uppercase tracking-widest text-brand-blue truncate">
                            <i className="fas fa-key mr-1.5" /> {pod.igloo_device_name || pod.igloo_device_id}
                          </p>
                          <p className="text-[9px] text-slate-400 font-bold truncate">{pod.igloo_device_id}</p>
                        </div>
                        <button onClick={() => unmapDevice(pod.slug)} disabled={busySlug === pod.slug} className="text-[9px] font-black uppercase tracking-widest text-red-600 hover:text-red-800 disabled:opacity-50" data-testid={`pods-unlink-${pod.slug}`}>
                          Unlink
                        </button>
                      </div>
                      {/* Battery indicator */}
                      {pod.battery_level != null && (
                        <div className="flex items-center gap-2 pt-1" data-testid={`pods-battery-${pod.slug}`}>
                          <BatteryPill level={pod.battery_level} />
                          <span className="text-[9px] text-slate-400 font-bold">
                            {pod.battery_updated_at ? `checked ${fmtDate(pod.battery_updated_at)}` : "not yet checked"}
                          </span>
                        </div>
                      )}
                    </div>
                  ) : showMapperFor === pod.slug ? (
                    <div className="space-y-2">
                      <p className="text-[10px] font-black uppercase tracking-widest text-brand-blue">Pick your Smart Keybox 3</p>
                      {iglooDevices?.loading ? (
                        <p className="text-xs font-bold text-slate-400"><i className="fas fa-spinner fa-spin mr-1" /> Loading devices…</p>
                      ) : iglooDevices?.error ? (
                        <p className="text-xs font-bold text-red-500">Couldn&apos;t reach igloohome. Verify credentials.</p>
                      ) : iglooDevices?.list?.length === 0 ? (
                        <p className="text-xs font-bold text-slate-500">No devices on this account. Add the Keybox in your igloohome portal first.</p>
                      ) : (
                        <div className="space-y-1 max-h-40 overflow-y-auto">
                          {iglooDevices?.list?.map((d) => (
                            <button
                              key={d.deviceId || d.id}
                              onClick={() => mapDevice(pod.slug, d)}
                              disabled={busySlug === pod.slug}
                              className="w-full text-left bg-slate-50 hover:bg-brand-blue hover:text-white transition-colors p-2 rounded-lg text-xs font-bold disabled:opacity-50"
                              data-testid={`igloo-pick-${d.deviceId || d.id}`}
                            >
                              <span className="font-black uppercase tracking-tight">{d.deviceName || d.name || d.type || "Keybox"}</span>
                              <span className="ml-2 opacity-60 font-mono text-[10px]">{d.deviceId || d.id}</span>
                            </button>
                          ))}
                        </div>
                      )}
                      <button onClick={() => setShowMapperFor(null)} className="text-[10px] font-black uppercase tracking-widest text-slate-400 hover:text-slate-700">Cancel</button>
                    </div>
                  ) : (
                    <button
                      onClick={() => { setShowMapperFor(pod.slug); if (!iglooDevices) loadIglooDevices(); }}
                      className="w-full bg-slate-100 hover:bg-brand-blue hover:text-white text-brand-blue py-2 rounded-lg font-black uppercase text-[10px] tracking-widest transition-colors"
                      data-testid={`pods-link-igloo-${pod.slug}`}
                    >
                      <i className="fas fa-plug mr-1.5" /> Link igloohome Keybox
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function BatteryPill({ level }) {
  const low = level < 20;
  const mid = level < 50;
  const cls = low ? "bg-red-100 text-red-800 border-red-300"
    : mid ? "bg-amber-100 text-amber-900 border-amber-300"
    : "bg-emerald-100 text-emerald-800 border-emerald-300";
  const icon = low ? "fa-battery-empty" : mid ? "fa-battery-half" : "fa-battery-three-quarters";
  return (
    <span className={`text-[10px] font-black uppercase tracking-widest border px-2 py-1 rounded-full ${cls}`}>
      <i className={`fas ${icon} mr-1.5`} /> {level}%{low && " · LOW"}
    </span>
  );
}


// =====================================================================
// Pod Network — list of pods → drill into ONE pod's Location/Equipment/Snapshot/Activity
// =====================================================================
// =====================================================================
// Financials — Transactions ledger + revenue metrics + refunds
// =====================================================================
function FinancialsSection() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [kind, setKind] = useState("");
  const [pod, setPod] = useState("");
  const [busy, setBusy] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (kind) params.set("kind", kind);
      if (pod) params.set("pod_slug", pod);
      const { data } = await api.get(`/admin/financials?${params.toString()}`);
      setData(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load financials");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [kind, pod]);  // eslint-disable-line react-hooks/exhaustive-deps

  const refund = async (session_id) => {
    const reason = prompt("Refund reason? (optional)") ?? "";
    if (!confirm("Fully refund this transaction? This cannot be undone.")) return;
    setBusy(session_id);
    try {
      await api.post(`/admin/financials/refund/${session_id}`, { reason });
      toast.success("Refund initiated");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Refund failed");
    } finally { setBusy(null); }
  };

  const exportCsv = () => {
    if (!data?.transactions?.length) return;
    const rows = [
      ["Date", "Kind", "Amount", "Currency", "Status", "Pod", "Customer", "Booking ID", "Session ID", "Refunded"],
      ...data.transactions.map((t) => [
        t.created_at,
        t.kind || "",
        (t.amount ?? 0).toFixed(2),
        (t.currency || "usd").toUpperCase(),
        t.payment_status || "",
        t.pod_slug || "",
        t.customer_name || t.user_email || "",
        t.booking_id || "",
        t.session_id || "",
        t.refunded_at ? "yes" : "no",
      ]),
    ];
    const csv = rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `financials-${new Date().toISOString().slice(0,10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  return (
    <section className="space-y-6" data-testid="financials-section">
      <header className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <p className="text-[10px] font-black uppercase tracking-[0.3em] text-brand-red">Money</p>
          <h2 className="text-3xl md:text-4xl font-black uppercase tracking-tightest text-brand-blue">Financials</h2>
          <p className="text-slate-500 text-sm font-bold mt-2">
            Revenue, deposits, and every Stripe transaction — one autonomous cockpit.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={exportCsv}
            disabled={!data?.transactions?.length}
            className="text-[10px] font-black uppercase tracking-widest bg-slate-100 hover:bg-brand-blue hover:text-white text-brand-blue px-4 py-2.5 rounded-xl transition-colors disabled:opacity-40"
            data-testid="financials-export-csv"
          >
            <i className="fas fa-file-csv mr-1.5" /> Export CSV
          </button>
          <button
            onClick={load}
            className="text-[10px] font-black uppercase tracking-widest bg-brand-red text-white hover:opacity-90 px-4 py-2.5 rounded-xl transition-opacity"
            data-testid="financials-refresh"
          >
            <i className="fas fa-arrows-rotate mr-1.5" /> Refresh
          </button>
        </div>
      </header>

      {loading ? (
        <div className="text-center py-16 text-slate-400 font-black uppercase text-xs tracking-widest">
          <i className="fas fa-spinner fa-spin mr-2" /> Loading financials…
        </div>
      ) : !data ? null : (
        <>
          {/* KPI CARDS */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="financials-kpis">
            <FinKpi label="Total Revenue" value={`$${data.summary.total_revenue.toLocaleString()}`} accent="text-brand-red" icon="fa-dollar-sign" />
            <FinKpi label="Paid Bookings" value={data.summary.paid_bookings} accent="text-brand-blue" icon="fa-check-circle" />
            <FinKpi label="Deposits Held" value={`$${data.summary.deposits_pending_amount.toFixed(0)}`} accent="text-amber-600" icon="fa-hand-holding-dollar" hint={`${data.summary.deposits_held_count} live holds`} />
            <FinKpi label="Deposits Captured" value={`$${data.summary.deposits_captured_amount.toFixed(0)}`} accent="text-emerald-600" icon="fa-lock" />
            <FinKpi label="Deposits Released" value={`$${data.summary.deposits_released_amount.toFixed(0)}`} accent="text-slate-500" icon="fa-unlock" />
            <FinKpi label="Late Fees Due" value={`$${data.summary.late_fees_outstanding.toFixed(0)}`} accent="text-red-600" icon="fa-triangle-exclamation" />
            <FinKpi label="Pending Checkouts" value={`$${data.summary.pending_checkout_amount.toFixed(0)}`} accent="text-slate-500" icon="fa-hourglass-half" />
            <FinKpi label="Stripe Mode" value={data.summary.stripe_live ? "LIVE" : "MOCK"} accent={data.summary.stripe_live ? "text-emerald-600" : "text-slate-400"} icon="fa-plug" />
          </div>

          {/* MONTHLY CHART + POD BREAKDOWN */}
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-white rounded-2xl border border-slate-100 p-5" data-testid="financials-monthly">
              <p className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-3">Revenue — Last 12 Months</p>
              {data.monthly.length === 0 ? (
                <p className="text-sm text-slate-400 font-bold">No revenue yet.</p>
              ) : (
                <MonthlyBars series={data.monthly} />
              )}
            </div>
            <div className="bg-white rounded-2xl border border-slate-100 p-5" data-testid="financials-by-pod">
              <p className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-3">Revenue By Pod</p>
              {data.by_pod.length === 0 ? (
                <p className="text-sm text-slate-400 font-bold">No revenue yet.</p>
              ) : (
                <div className="space-y-2">
                  {data.by_pod.map((row) => {
                    const max = Math.max(...data.by_pod.map((r) => r.revenue), 1);
                    const pct = (row.revenue / max) * 100;
                    return (
                      <div key={row.pod_slug} data-testid={`pod-rev-${row.pod_slug}`}>
                        <div className="flex justify-between text-xs font-black uppercase tracking-tight">
                          <span className="text-brand-blue">{row.pod_slug}</span>
                          <span className="text-brand-red">${row.revenue.toLocaleString()} <span className="text-slate-400">· {row.bookings}</span></span>
                        </div>
                        <div className="h-2 bg-slate-100 rounded-full mt-1 overflow-hidden">
                          <div className="h-full bg-gradient-to-r from-brand-gold to-brand-red" style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* FILTERS */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Filter</span>
            <select value={kind} onChange={(e) => setKind(e.target.value)} className="text-xs font-bold px-3 py-2 rounded-lg border border-slate-200" data-testid="financials-filter-kind">
              <option value="">All kinds</option>
              <option value="rental">Rental</option>
              <option value="addon">Add-on</option>
              <option value="deposit_hold">Deposit Hold</option>
              <option value="deposit_capture">Deposit Capture</option>
            </select>
            <select value={pod} onChange={(e) => setPod(e.target.value)} className="text-xs font-bold px-3 py-2 rounded-lg border border-slate-200" data-testid="financials-filter-pod">
              <option value="">All pods</option>
              {data.by_pod.map((p) => <option key={p.pod_slug} value={p.pod_slug}>{p.pod_slug}</option>)}
            </select>
          </div>

          {/* TRANSACTIONS TABLE */}
          <div className="bg-white rounded-2xl border border-slate-100 overflow-x-auto" data-testid="financials-txn-table">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr className="text-left text-[10px] font-black uppercase tracking-widest text-slate-500">
                  <th className="p-3">Date</th>
                  <th className="p-3">Kind</th>
                  <th className="p-3">Amount</th>
                  <th className="p-3">Status</th>
                  <th className="p-3">Pod</th>
                  <th className="p-3">Customer</th>
                  <th className="p-3">Session</th>
                  <th className="p-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {data.transactions.length === 0 ? (
                  <tr><td colSpan={8} className="p-8 text-center text-slate-400 font-bold">No transactions match this filter.</td></tr>
                ) : data.transactions.map((t) => (
                  <tr key={t.id} className="border-t border-slate-100" data-testid={`financials-txn-${t.id}`}>
                    <td className="p-3 text-xs text-slate-600 font-bold whitespace-nowrap">
                      {t.created_at ? new Date(t.created_at).toLocaleDateString("en-US", { month:"short", day:"numeric", year:"2-digit" }) : "—"}
                    </td>
                    <td className="p-3">
                      <span className="text-[10px] font-black uppercase tracking-widest bg-slate-100 text-brand-blue px-2 py-1 rounded-full">{t.kind || "rental"}</span>
                    </td>
                    <td className="p-3 font-black text-brand-blue">${(t.amount ?? 0).toFixed(2)}</td>
                    <td className="p-3">
                      <FinStatusPill status={t.payment_status} refunded={!!t.refunded_at} />
                    </td>
                    <td className="p-3 text-xs font-bold text-slate-500">{t.pod_slug || (t.booking_id ? "park-hill" : "—")}</td>
                    <td className="p-3 text-xs font-bold text-brand-blue truncate max-w-[180px]">{t.customer_name || t.user_email || "—"}</td>
                    <td className="p-3 text-[10px] font-mono text-slate-400 truncate max-w-[140px]">{t.session_id?.slice(-16)}</td>
                    <td className="p-3 text-right">
                      {t.payment_status === "paid" && !t.refunded_at ? (
                        <button
                          onClick={() => refund(t.session_id)}
                          disabled={busy === t.session_id}
                          className="text-[10px] font-black uppercase tracking-widest text-red-600 hover:text-red-800 disabled:opacity-50"
                          data-testid={`refund-btn-${t.id}`}
                        >
                          {busy === t.session_id ? <><i className="fas fa-spinner fa-spin" /></> : <><i className="fas fa-rotate-left mr-1" /> Refund</>}
                        </button>
                      ) : t.refunded_at ? (
                        <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">Refunded</span>
                      ) : (
                        <span className="text-[10px] text-slate-300">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}

function FinKpi({ label, value, accent = "text-brand-blue", icon, hint }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-100 p-4" data-testid={`fin-kpi-${label.toLowerCase().replace(/\s+/g,'-')}`}>
      <div className="flex items-center justify-between">
        <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">{label}</p>
        {icon && <i className={`fas ${icon} ${accent} opacity-70`} />}
      </div>
      <p className={`text-2xl font-black tracking-tight mt-1 ${accent}`}>{value}</p>
      {hint && <p className="text-[10px] font-bold text-slate-400 mt-1">{hint}</p>}
    </div>
  );
}

function FinStatusPill({ status, refunded }) {
  if (refunded) return <span className="text-[10px] font-black uppercase tracking-widest bg-slate-200 text-slate-700 px-2 py-1 rounded-full">Refunded</span>;
  const map = {
    paid: { c: "bg-emerald-100 text-emerald-800", l: "Paid" },
    initiated: { c: "bg-amber-100 text-amber-800", l: "Pending" },
    expired: { c: "bg-slate-100 text-slate-500", l: "Expired" },
  };
  const s = map[status] || { c: "bg-slate-100 text-slate-500", l: status || "—" };
  return <span className={`text-[10px] font-black uppercase tracking-widest ${s.c} px-2 py-1 rounded-full`}>{s.l}</span>;
}

function MonthlyBars({ series }) {
  const max = Math.max(...series.map((s) => s.revenue), 1);
  return (
    <div className="flex items-end gap-2 h-40" data-testid="financials-monthly-bars">
      {series.map((row) => {
        const h = Math.round((row.revenue / max) * 100);
        return (
          <div key={row.month} className="flex-1 flex flex-col items-center justify-end min-w-0" title={`$${row.revenue.toFixed(0)} in ${row.month}`}>
            <div className="w-full bg-gradient-to-t from-brand-red to-brand-gold rounded-t" style={{ height: `${Math.max(h, 4)}%` }} />
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400 mt-1 truncate w-full text-center">{row.month.slice(5)}</p>
          </div>
        );
      })}
    </div>
  );
}


function PodNetworkAdminSection() {
  const [selected, setSelected] = useState(null);  // null = list view, otherwise a pod object
  return (
    <section className="space-y-6" data-testid="pod-network-section">
      <header>
        <p className="text-[10px] font-black uppercase tracking-[0.3em] text-brand-red">Network</p>
        <h2 className="text-3xl md:text-4xl font-black uppercase tracking-tightest text-brand-blue">Pod Network</h2>
        <p className="text-slate-500 text-sm font-bold mt-2">
          Everything about your fleet — where they live, what they hold, how they&apos;re doing.
        </p>
      </header>

      {selected
        ? <PodDetailAdminView pod={selected} onBack={() => setSelected(null)} />
        : <PodNetworkListView onSelect={setSelected} />
      }
    </section>
  );
}

// -------- LIST VIEW: grid of pod cards ---------
function PodNetworkListView({ onSelect }) {
  const [pods, setPods] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const [p, b, inv] = await Promise.all([
        api.get("/pods"),
        api.get("/admin/bookings"),
        api.get("/inventory"),
      ]);
      setPods(p.data.items || []);
      setBookings(b.data || []);
      setItems(inv.data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, []);

  const resolvePhoto = (url) => {
    if (!url) return "";
    if (url.startsWith("/api")) return `${process.env.REACT_APP_BACKEND_URL}${url}`;
    return url;
  };

  return (
    <div className="space-y-4" data-testid="pod-network-list">
      <div className="flex justify-end gap-2">
        <button
          onClick={() => setShowAdd(true)}
          className="text-[10px] font-black uppercase tracking-widest bg-brand-red text-white hover:opacity-90 px-4 py-2.5 rounded-xl transition-opacity"
          data-testid="pods-add-btn"
        >
          <i className="fas fa-plus mr-1.5" /> Add Pod
        </button>
        <button
          onClick={async () => {
            try { await api.post("/admin/pods/battery-check"); toast.success("Battery check complete"); refresh(); }
            catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Battery check failed"); }
          }}
          className="text-[10px] font-black uppercase tracking-widest bg-slate-100 hover:bg-brand-blue hover:text-white text-brand-blue px-4 py-2.5 rounded-xl transition-colors"
          data-testid="pods-battery-check-now"
        >
          <i className="fas fa-battery-half mr-1.5" /> Check Batteries Now
        </button>
      </div>

      {showAdd && <AddPodDialog open={showAdd} onClose={() => setShowAdd(false)} onCreated={refresh} />}

      {loading ? (
        <div className="text-center py-16 text-slate-400 font-black uppercase text-xs tracking-widest">
          <i className="fas fa-spinner fa-spin mr-2" /> Loading pods…
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {pods.map((pod) => {
            const podBookings = bookings.filter((b) => (b.pod_slug || "park-hill") === pod.slug);
            const paid = podBookings.filter((b) => ["paid", "active", "completed"].includes(b.status));
            const revenue = paid.reduce((s, b) => s + (b.total || 0), 0);
            const active = podBookings.filter((b) => b.status === "active").length;
            const stockTotal = items.reduce((s, it) => s + ((it.stock_by_pod || {})[pod.slug] || 0), 0);
            return (
              <button
                key={pod.id}
                onClick={() => onSelect(pod)}
                className="text-left bg-white rounded-[24px] border border-slate-100 overflow-hidden shadow-sm hover:border-brand-gold hover:shadow-md transition-all group"
                data-testid={`pod-network-card-${pod.slug}`}
              >
                <div className="relative h-40 bg-slate-100">
                  <img src={resolvePhoto(pod.photo_url)} alt={pod.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform" onError={(e) => { e.currentTarget.style.opacity = "0.3"; }} />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  <div className="absolute top-3 left-3 flex gap-2">
                    {pod.status === "active" ? (
                      <span className="bg-emerald-500 text-white text-[9px] font-black uppercase tracking-widest px-2 py-1 rounded-full">Live</span>
                    ) : pod.status === "opening_soon" ? (
                      <span className="bg-brand-gold text-brand-blue text-[9px] font-black uppercase tracking-widest px-2 py-1 rounded-full">Opening Soon</span>
                    ) : (
                      <span className="bg-slate-500 text-white text-[9px] font-black uppercase tracking-widest px-2 py-1 rounded-full">Inactive</span>
                    )}
                    {pod.battery_level != null && pod.battery_level < 20 && (
                      <span className="bg-red-500 text-white text-[9px] font-black uppercase tracking-widest px-2 py-1 rounded-full">
                        <i className="fas fa-battery-empty mr-1" /> {pod.battery_level}%
                      </span>
                    )}
                  </div>
                  <div className="absolute bottom-3 left-3 right-3">
                    <p className="text-[10px] font-black uppercase tracking-widest text-brand-gold">{pod.neighborhood}</p>
                    <h3 className="text-xl font-black uppercase tracking-tight text-white leading-none">{pod.name}</h3>
                  </div>
                </div>
                <div className="p-4 grid grid-cols-4 gap-2">
                  <MiniStat label="Rev" value={`$${revenue.toFixed(0)}`} />
                  <MiniStat label="Books" value={podBookings.length} />
                  <MiniStat label="Active" value={active} />
                  <MiniStat label="Stock" value={stockTotal} />
                </div>
                <div className="border-t border-slate-100 px-4 py-2 flex items-center justify-between text-[10px] font-black uppercase tracking-widest">
                  <span className="text-slate-400">{pod.igloo_device_id ? <><i className="fas fa-key mr-1 text-brand-blue" /> Linked</> : "Not linked"}</span>
                  <span className="text-brand-red group-hover:translate-x-1 transition-transform">
                    Manage <i className="fas fa-arrow-right ml-1" />
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function MiniStat({ label, value }) {
  return (
    <div className="text-center">
      <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">{label}</p>
      <p className="text-sm font-black text-brand-blue tracking-tight truncate">{value}</p>
    </div>
  );
}

// -------- DETAIL VIEW: one pod, 4 tabs ---------
function PodDetailAdminView({ pod: initialPod, onBack }) {
  const [pod, setPod] = useState(initialPod);
  const [tab, setTab] = useState("location");
  const TABS = [
    { id: "location",  label: "Location",  icon: "fa-map-pin" },
    { id: "equipment", label: "Equipment", icon: "fa-box-open" },
    { id: "snapshot",  label: "Snapshot",  icon: "fa-chart-pie" },
    { id: "activity",  label: "Activity",  icon: "fa-bolt" },
  ];

  const refresh = async () => {
    try {
      const { data } = await api.get(`/pods/${pod.slug}`);
      setPod(data);
    } catch { /* ignore */ }
  };

  return (
    <div className="space-y-5" data-testid={`pod-detail-admin-${pod.slug}`}>
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <button
          onClick={onBack}
          className="text-[10px] font-black uppercase tracking-widest text-brand-blue hover:text-brand-red transition-colors"
          data-testid="pod-detail-back"
        >
          <i className="fas fa-arrow-left mr-1.5" /> Back to Network
        </button>
        <div className="text-right">
          <p className="text-[10px] font-black uppercase tracking-widest text-brand-red">{pod.neighborhood}</p>
          <h3 className="text-2xl font-black uppercase tracking-tightest text-brand-blue">{pod.name}</h3>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 border-b border-slate-100">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2.5 text-[10px] font-black uppercase tracking-widest transition-colors ${tab === t.id ? "text-brand-red border-b-2 border-brand-red" : "text-slate-500 hover:text-brand-blue"}`}
            data-testid={`pod-detail-tab-${t.id}`}
          >
            <i className={`fas ${t.icon} mr-1.5`} /> {t.label}
          </button>
        ))}
      </div>

      {tab === "location"  && <PodLocationOneCard pod={pod} onRefresh={refresh} onDeleted={onBack} />}
      {tab === "equipment" && <PodEquipmentTab podSlugFilter={pod.slug} />}
      {tab === "snapshot"  && <PodSnapshotTab podSlugFilter={pod.slug} />}
      {tab === "activity"  && <PodActivityTab podSlugFilter={pod.slug} />}
    </div>
  );
}

// ONE pod's editable location card (photos + igloohome + editable status/hours)
function PodLocationOneCard({ pod, onRefresh, onDeleted }) {
  const [busy, setBusy] = useState(false);
  const [iglooDevices, setIglooDevices] = useState(null);
  const [showMapper, setShowMapper] = useState(false);
  const [form, setForm] = useState({
    name: pod.name || "",
    neighborhood: pod.neighborhood || "",
    address: pod.address || "",
    hours: pod.hours || "",
    tagline: pod.tagline || "",
    status: pod.status || "opening_soon",
    launch_date: pod.launch_date || "",
    lat: pod.lat ?? "",
    lng: pod.lng ?? "",
  });

  const resolvePhoto = (url) => {
    if (!url) return "";
    if (url.startsWith("/api")) return `${process.env.REACT_APP_BACKEND_URL}${url}`;
    return url;
  };

  const save = async () => {
    setBusy(true);
    try {
      const body = { ...form, lat: form.lat === "" ? null : parseFloat(form.lat), lng: form.lng === "" ? null : parseFloat(form.lng) };
      await api.patch(`/admin/pods/${pod.slug}`, body);
      toast.success("Location updated");
      onRefresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally { setBusy(false); }
  };

  const del = async () => {
    if (!confirm(`Delete ${pod.name}? This is permanent.`)) return;
    setBusy(true);
    try {
      await api.delete(`/admin/pods/${pod.slug}`);
      toast.success("Pod deleted");
      onDeleted();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete blocked");
    } finally { setBusy(false); }
  };

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post(`/admin/pods/${pod.slug}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Photo uploaded");
      onRefresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed");
    } finally { setBusy(false); }
  };

  const genPhoto = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/pods/${pod.slug}/generate-photo`);
      toast.success("AI render generated");
      onRefresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Render failed");
    } finally { setBusy(false); }
  };

  const loadIgloo = async () => {
    setIglooDevices({ loading: true, list: [] });
    try {
      const { data } = await api.get("/admin/igloo/devices");
      setIglooDevices({ loading: false, list: data.devices || [] });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "igloohome unreachable");
      setIglooDevices({ loading: false, list: [], error: true });
    }
  };

  const linkDevice = async (dev) => {
    setBusy(true);
    try {
      await api.post(`/admin/pods/${pod.slug}/igloo-device`, {
        device_id: dev.deviceId || dev.id, device_name: dev.deviceName || dev.name || dev.type,
      });
      toast.success("igloohome device linked");
      setShowMapper(false); onRefresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Link failed");
    } finally { setBusy(false); }
  };

  const unlinkDevice = async () => {
    if (!confirm("Unlink the igloohome device?")) return;
    setBusy(true);
    try { await api.delete(`/admin/pods/${pod.slug}/igloo-device`); toast.success("Unlinked"); onRefresh(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };

  const resetBatteries = async () => {
    if (!confirm(`Mark ${pod.name} batteries as freshly replaced?\n\nThis sets the level to 100% until igloohome's app syncs a real reading.`)) return;
    setBusy(true);
    try {
      await api.post(`/admin/pods/${pod.slug}/battery-reset`);
      toast.success("Batteries marked as fresh (100%)");
      onRefresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Reset failed");
    } finally { setBusy(false); }
  };

  const [unlockPin, setUnlockPin] = useState(null);
  const [unlockHours, setUnlockHours] = useState(1);
  const [nowTick, setNowTick] = useState(Date.now());

  // Hydrate the last-generated PIN from localStorage on mount / pod switch
  useEffect(() => {
    try {
      const raw = localStorage.getItem(`pod_unlock_pin_${pod.slug}`);
      if (!raw) { setUnlockPin(null); return; }
      const cached = JSON.parse(raw);
      if (cached?.valid_to && new Date(cached.valid_to).getTime() > Date.now()) {
        setUnlockPin(cached);
      } else {
        localStorage.removeItem(`pod_unlock_pin_${pod.slug}`);
        setUnlockPin(null);
      }
    } catch { setUnlockPin(null); }
  }, [pod.slug]);

  // Ticker for the live countdown; also auto-clears expired PIN
  useEffect(() => {
    if (!unlockPin) return undefined;
    const t = setInterval(() => {
      const nowMs = Date.now();
      setNowTick(nowMs);
      if (unlockPin?.valid_to && new Date(unlockPin.valid_to).getTime() <= nowMs) {
        localStorage.removeItem(`pod_unlock_pin_${pod.slug}`);
        setUnlockPin(null);
      }
    }, 1000);
    return () => clearInterval(t);
  }, [unlockPin, pod.slug]);

  const unlockNow = async () => {
    const reason = prompt("Reason (optional — appears in audit log):", "admin unlock") || "admin unlock";
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/pods/${pod.slug}/unlock-now`, { hours: Number(unlockHours), reason });
      setUnlockPin(data);
      localStorage.setItem(`pod_unlock_pin_${pod.slug}`, JSON.stringify(data));
      toast.success(`PIN generated: ${data.pin}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Unlock failed");
    } finally { setBusy(false); }
  };

  const clearUnlockPin = () => {
    localStorage.removeItem(`pod_unlock_pin_${pod.slug}`);
    setUnlockPin(null);
  };

  const bind = (k) => ({ value: form[k], onChange: (e) => setForm({ ...form, [k]: e.target.value }) });

  return (
    <div className="grid md:grid-cols-2 gap-6" data-testid="pod-location-one">
      {/* Photo + actions */}
      <div className="space-y-3">
        <div className="relative h-64 rounded-2xl overflow-hidden bg-slate-100">
          <img src={resolvePhoto(pod.photo_url)} alt={pod.name} className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.opacity = "0.3"; }} />
        </div>
        <div className="flex gap-2">
          <label className="flex-1 cursor-pointer bg-brand-blue text-white text-center py-2.5 rounded-xl font-black uppercase text-[10px] tracking-widest hover:opacity-90" data-testid="detail-upload-btn">
            <input type="file" accept="image/*" className="hidden" disabled={busy} onChange={(e) => upload(e.target.files?.[0])} />
            <i className="fas fa-upload mr-1.5" /> Upload
          </label>
          <button onClick={genPhoto} disabled={busy} className="flex-1 bg-brand-gold text-brand-blue py-2.5 rounded-xl font-black uppercase text-[10px] tracking-widest hover:bg-amber-300 disabled:opacity-50" data-testid="detail-generate-btn">
            <i className="fas fa-wand-magic-sparkles mr-1.5" /> AI Render
          </button>
        </div>

        {/* igloohome */}
        <div className="bg-slate-50 rounded-xl p-3 space-y-2">
          <p className="text-[10px] font-black uppercase tracking-widest text-brand-blue"><i className="fas fa-key mr-1.5" /> igloohome Keybox</p>
          {pod.igloo_device_id ? (
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0 flex-1">
                <p className="text-xs font-black truncate">{pod.igloo_device_name || pod.igloo_device_id}</p>
                <p className="text-[9px] text-slate-400 font-mono truncate">{pod.igloo_device_id}</p>
                {pod.battery_level != null && (
                  <div className="mt-1 flex items-center gap-2 flex-wrap">
                    <BatteryPill level={pod.battery_level} />
                    <button
                      onClick={resetBatteries}
                      disabled={busy}
                      className="text-[9px] font-black uppercase tracking-widest text-brand-blue hover:text-brand-red disabled:opacity-50"
                      data-testid="battery-reset-btn"
                      title="Manually mark batteries as freshly replaced"
                    >
                      <i className="fas fa-battery-full mr-1" /> Batteries Replaced
                    </button>
                  </div>
                )}
                {pod.battery_manually_set_at && (
                  <p className="text-[9px] text-emerald-600 font-bold mt-1">
                    <i className="fas fa-check-circle mr-1" />
                    Manually reset {new Date(pod.battery_manually_set_at).toLocaleDateString("en-US",{month:"short",day:"numeric"})}. Real reading will resume once someone opens the igloohome app near the keybox.
                  </p>
                )}
              </div>
              <button onClick={unlinkDevice} disabled={busy} className="text-[9px] font-black uppercase tracking-widest text-red-600 hover:text-red-800 disabled:opacity-50">Unlink</button>
            </div>
          ) : showMapper ? (
            iglooDevices?.loading ? <p className="text-xs font-bold text-slate-400"><i className="fas fa-spinner fa-spin mr-1" /> Loading…</p>
            : iglooDevices?.list?.length === 0 ? <p className="text-xs font-bold text-slate-500">No devices on this account.</p>
            : <div className="space-y-1 max-h-40 overflow-y-auto">
                {iglooDevices?.list?.map((d) => (
                  <button key={d.deviceId || d.id} onClick={() => linkDevice(d)} disabled={busy} className="w-full text-left bg-white hover:bg-brand-blue hover:text-white p-2 rounded-lg text-xs font-bold disabled:opacity-50">
                    <span className="font-black uppercase tracking-tight">{d.deviceName || d.name || d.type || "Keybox"}</span>
                    <span className="ml-2 opacity-60 font-mono text-[10px]">{d.deviceId || d.id}</span>
                  </button>
                ))}
              </div>
          ) : (
            <button onClick={() => { setShowMapper(true); loadIgloo(); }} className="w-full bg-white hover:bg-brand-blue hover:text-white text-brand-blue py-2 rounded-lg font-black uppercase text-[10px] tracking-widest" data-testid="detail-link-igloo">
              <i className="fas fa-plug mr-1.5" /> Link Keybox
            </button>
          )}
        </div>

        {/* Unlock Now — generates a live algoPIN */}
        {pod.igloo_device_id && (
          <div className="bg-gradient-to-br from-brand-red to-red-700 text-white rounded-xl p-4 space-y-3" data-testid="unlock-now-panel">
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="text-[10px] font-black uppercase tracking-widest text-brand-gold"><i className="fas fa-bolt mr-1.5" /> Unlock Pod Now</p>
                <p className="text-[10px] font-bold opacity-80 mt-1">
                  Generates a live PIN valid at the keybox. igloohome Keybox 3 has no online-open API — walk up and enter the PIN.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-[10px] font-black uppercase tracking-widest opacity-80">Valid for</label>
              <select
                value={unlockHours}
                onChange={(e) => setUnlockHours(e.target.value)}
                className="text-xs font-black px-2 py-1 rounded text-brand-blue bg-white"
                data-testid="unlock-hours-select"
              >
                <option value={1}>1 hour</option>
                <option value={2}>2 hours</option>
                <option value={3}>3 hours</option>
              </select>
              <button
                onClick={unlockNow}
                disabled={busy}
                className="flex-1 bg-brand-gold text-brand-blue py-2 rounded-lg font-black uppercase text-[10px] tracking-widest hover:bg-amber-300 disabled:opacity-50"
                data-testid="unlock-now-btn"
              >
                {busy ? <><i className="fas fa-spinner fa-spin mr-1.5" /> Generating…</> : <><i className="fas fa-unlock mr-1.5" /> Generate PIN</>}
              </button>
            </div>
            {unlockPin && (() => {
              const remainingMs = Math.max(0, new Date(unlockPin.valid_to).getTime() - nowTick);
              const mins = Math.floor(remainingMs / 60000);
              const secs = Math.floor((remainingMs % 60000) / 1000);
              const totalMs = new Date(unlockPin.valid_to).getTime() - new Date(unlockPin.valid_from).getTime();
              const pctLeft = totalMs > 0 ? Math.max(0, Math.min(100, (remainingMs / totalMs) * 100)) : 0;
              return (
                <div className="bg-black/30 rounded-lg p-3 space-y-2" data-testid="unlock-pin-result">
                  <div className="flex items-center justify-between">
                    <p className="text-[9px] font-black uppercase tracking-widest text-brand-gold">Enter this on the keypad</p>
                    <button
                      onClick={clearUnlockPin}
                      className="text-[9px] font-black uppercase tracking-widest text-white/60 hover:text-white"
                      data-testid="unlock-pin-clear"
                    >
                      <i className="fas fa-times mr-1" /> Clear
                    </button>
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-3xl font-black tracking-widest font-mono">{unlockPin.pin}</p>
                    <button
                      onClick={() => { navigator.clipboard.writeText(unlockPin.pin); toast.success("Copied"); }}
                      className="text-[10px] font-black uppercase tracking-widest bg-white/20 hover:bg-white/30 px-2 py-1 rounded"
                      data-testid="unlock-pin-copy"
                    >
                      <i className="fas fa-copy mr-1" /> Copy
                    </button>
                  </div>
                  <div className="h-1 bg-white/20 rounded-full overflow-hidden">
                    <div className="h-full bg-brand-gold transition-all" style={{ width: `${pctLeft}%` }} />
                  </div>
                  <div className="flex items-center justify-between text-[10px] font-bold">
                    <span className="opacity-80">
                      Valid {new Date(unlockPin.valid_from).toLocaleTimeString([], {hour:"numeric", minute:"2-digit"})} — {new Date(unlockPin.valid_to).toLocaleTimeString([], {hour:"numeric", minute:"2-digit"})}
                    </span>
                    <span className="font-mono text-brand-gold" data-testid="unlock-pin-countdown">
                      {remainingMs > 0 ? `${mins}m ${String(secs).padStart(2,"0")}s left` : "Expired"}
                    </span>
                  </div>
                </div>
              );
            })()}
          </div>
        )}
      </div>

      {/* Editable fields */}
      <div className="space-y-3">
        <FormField label="Name" {...bind("name")} testId="detail-form-name" />
        <FormField label="Neighborhood" {...bind("neighborhood")} testId="detail-form-neighborhood" />
        <FormField label="Address" {...bind("address")} testId="detail-form-address" />
        <div className="grid grid-cols-2 gap-3">
          <FormField label="Latitude"  {...bind("lat")} testId="detail-form-lat" />
          <FormField label="Longitude" {...bind("lng")} testId="detail-form-lng" />
        </div>
        <div>
          <label className="block text-[10px] font-black uppercase tracking-widest text-brand-blue mb-1">Status</label>
          <select {...bind("status")} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm font-bold" data-testid="detail-form-status">
            <option value="opening_soon">Opening Soon</option>
            <option value="active">Active (Live)</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
        <FormField label="Hours" {...bind("hours")} testId="detail-form-hours" />
        <FormField label="Tagline" {...bind("tagline")} testId="detail-form-tagline" />
        <FormField label="Launch Date" {...bind("launch_date")} testId="detail-form-launch" />

        <div className="flex flex-wrap gap-2 pt-3 border-t border-slate-100">
          <button onClick={save} disabled={busy} className="btn-primary px-5 py-2.5 rounded-xl font-black uppercase text-[10px] tracking-widest disabled:opacity-50" data-testid="detail-save-btn">
            {busy ? <><i className="fas fa-spinner fa-spin mr-2" /> Saving</> : <><i className="fas fa-save mr-2" /> Save Location</>}
          </button>
          <button onClick={del} disabled={busy} className="bg-red-50 text-red-700 px-4 py-2.5 rounded-xl font-black uppercase text-[10px] tracking-widest hover:bg-red-100 disabled:opacity-50" data-testid="detail-delete-btn">
            <i className="fas fa-trash mr-2" /> Delete Pod
          </button>
        </div>
      </div>
    </div>
  );
}

function PodEquipmentTab({ podSlugFilter } = {}) {
  const [pods, setPods] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const load = async () => {
    setLoading(true);
    try {
      const [p, inv] = await Promise.all([
        api.get("/pods"),
        api.get("/inventory"),  // admin call — includes stock_by_pod
      ]);
      setPods(p.data.items || []);
      setItems(inv.data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const saveStock = async (itemId, podSlug, value) => {
    setSavingId(`${itemId}-${podSlug}`);
    try {
      const item = items.find((x) => x.id === itemId);
      const next = { ...(item.stock_by_pod || {}), [podSlug]: Math.max(0, parseInt(value || "0", 10)) };
      await api.patch(`/admin/inventory/${itemId}`, { stock_by_pod: next });
      toast.success(`${item.name} — ${podSlug} stock updated`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally { setSavingId(null); }
  };

  if (loading) return <div className="text-center py-16 text-slate-400 font-black uppercase text-xs tracking-widest"><i className="fas fa-spinner fa-spin mr-2" /> Loading…</div>;
  const podsToShow = podSlugFilter ? pods.filter((p) => p.slug === podSlugFilter) : pods;
  return (
    <div className="bg-white rounded-2xl border border-slate-100 overflow-x-auto" data-testid="pod-equipment-table">
      <table className="w-full text-sm">
        <thead className="bg-slate-50">
          <tr className="text-left text-[10px] font-black uppercase tracking-widest text-slate-500">
            <th className="p-3">Item</th>
            {podsToShow.map((p) => (
              <th key={p.slug} className="p-3 text-center">
                {p.name}
                <span className="block text-[9px] font-bold text-slate-400 mt-0.5">{p.status === "active" ? "Live" : "Soon"}</span>
              </th>
            ))}
            {!podSlugFilter && <th className="p-3 text-center">Network</th>}
          </tr>
        </thead>
        <tbody>
          {items.map((it) => {
            const byPod = it.stock_by_pod || {};
            const total = Object.values(byPod).reduce((a, b) => a + (Number(b) || 0), 0);
            return (
              <tr key={it.id} className="border-t border-slate-100">
                <td className="p-3 font-black text-brand-blue uppercase text-xs tracking-tight">{it.name}</td>
                {podsToShow.map((p) => (
                  <td key={p.slug} className="p-3 text-center">
                    <input
                      type="number"
                      min={0}
                      defaultValue={byPod[p.slug] ?? 0}
                      onBlur={(e) => {
                        const val = parseInt(e.target.value || "0", 10);
                        if (val !== (byPod[p.slug] ?? 0)) saveStock(it.id, p.slug, val);
                      }}
                      className="w-16 text-center rounded-lg border border-slate-200 px-2 py-1 text-sm font-black focus:border-brand-blue focus:outline-none"
                      data-testid={`stock-${it.id}-${p.slug}`}
                      disabled={savingId === `${it.id}-${p.slug}`}
                    />
                  </td>
                ))}
                {!podSlugFilter && <td className="p-3 text-center font-black text-brand-red">{total}</td>}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function PodSnapshotTab({ podSlugFilter } = {}) {
  const [pods, setPods] = useState([]);
  const [items, setItems] = useState([]);
  const [bookings, setBookings] = useState([]);
  useEffect(() => {
    Promise.all([api.get("/pods"), api.get("/inventory"), api.get("/admin/bookings")])
      .then(([p, inv, b]) => { setPods(p.data.items || []); setItems(inv.data || []); setBookings(b.data || []); })
      .catch(() => {});
  }, []);

  const filteredPods = podSlugFilter ? pods.filter((p) => p.slug === podSlugFilter) : pods;
  const podStats = filteredPods.map((p) => {
    const podBookings = bookings.filter((b) => (b.pod_slug || "park-hill") === p.slug);
    const paid = podBookings.filter((b) => ["paid", "active", "completed"].includes(b.status));
    const revenue = paid.reduce((sum, b) => sum + (b.total || 0), 0);
    const active = podBookings.filter((b) => b.status === "active").length;
    const stockTotal = items.reduce((sum, it) => sum + ((it.stock_by_pod || {})[p.slug] || 0), 0);
    return { pod: p, bookings: podBookings.length, revenue, active, stockTotal };
  });

  return (
    <div className={`grid gap-4 ${podSlugFilter ? "md:grid-cols-1" : "md:grid-cols-2 lg:grid-cols-3"}`} data-testid="pod-snapshot-grid">
      {podStats.map(({ pod, bookings: bc, revenue, active, stockTotal }) => (
        <div key={pod.slug} className="bg-white rounded-2xl border border-slate-100 p-5 space-y-3" data-testid={`snapshot-card-${pod.slug}`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[10px] font-black uppercase tracking-widest text-brand-red">{pod.neighborhood}</p>
              <h3 className="text-lg font-black uppercase tracking-tight text-brand-blue">{pod.name}</h3>
            </div>
            {pod.status === "active"
              ? <span className="bg-emerald-100 text-emerald-800 text-[9px] font-black uppercase tracking-widest px-2 py-1 rounded-full">Live</span>
              : <span className="bg-amber-100 text-amber-900 text-[9px] font-black uppercase tracking-widest px-2 py-1 rounded-full">Soon</span>}
          </div>
          <div className="grid grid-cols-2 gap-2 pt-2">
            <StatBox label="Revenue" value={`$${revenue.toFixed(0)}`} />
            <StatBox label="Bookings" value={bc} />
            <StatBox label="Active Now" value={active} />
            <StatBox label="Items in Stock" value={stockTotal} />
          </div>
        </div>
      ))}
    </div>
  );
}

function StatBox({ label, value }) {
  return (
    <div className="bg-slate-50 rounded-xl p-3">
      <p className="text-[9px] font-black uppercase tracking-widest text-slate-500">{label}</p>
      <p className="text-xl font-black text-brand-blue tracking-tight">{value}</p>
    </div>
  );
}

function PodActivityTab({ podSlugFilter } = {}) {
  const [feed, setFeed] = useState([]);
  useEffect(() => {
    api.get("/admin/bookings")
      .then(({ data }) => {
        // Build a synthetic activity feed from booking + access-code timestamps
        const events = [];
        data.forEach((b) => {
          const slug = b.pod_slug || "park-hill";
          if (podSlugFilter && slug !== podSlugFilter) return;
          if (b.access_codes_sent_at) events.push({ ts: b.access_codes_sent_at, slug, kind: "codes_sent", label: `PINs sent — ${b.customer_name || b.user_email}` });
          if (b.lock_confirmation_at) events.push({ ts: b.lock_confirmation_at, slug, kind: "locked", label: `Pod locked — ${b.customer_name || b.user_email}` });
          if (b.codes_deleted_at)     events.push({ ts: b.codes_deleted_at,     slug, kind: "cleaned",label: `Codes deleted — booking closed` });
          if (b.paid_at)              events.push({ ts: b.paid_at,              slug, kind: "paid",   label: `Booking paid — $${(b.total || 0).toFixed(0)}` });
        });
        events.sort((a, b) => (a.ts < b.ts ? 1 : -1));
        setFeed(events.slice(0, 50));
      })
      .catch(() => {});
  }, [podSlugFilter]);

  const KIND = {
    codes_sent: { icon: "fa-key",         color: "text-amber-600" },
    locked:     { icon: "fa-lock",        color: "text-emerald-600" },
    cleaned:    { icon: "fa-check-double",color: "text-slate-400" },
    paid:       { icon: "fa-dollar-sign", color: "text-brand-blue" },
  };

  if (feed.length === 0) {
    return <div className="text-center py-16 text-slate-400 font-bold" data-testid="pod-activity-empty">No pod activity yet.</div>;
  }
  return (
    <div className="bg-white rounded-2xl border border-slate-100 divide-y divide-slate-100" data-testid="pod-activity-feed">
      {feed.map((e, idx) => {
        const meta = KIND[e.kind] || { icon: "fa-circle", color: "text-slate-400" };
        return (
          <div key={idx} className="flex items-center gap-4 p-4">
            <div className={`w-10 h-10 rounded-xl bg-slate-50 flex items-center justify-center flex-shrink-0 ${meta.color}`}>
              <i className={`fas ${meta.icon}`} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-black text-brand-blue uppercase text-sm tracking-tight truncate">{e.label}</p>
              <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">{e.slug} · {new Date(e.ts).toLocaleString("en-US", { month:"short", day:"numeric", hour:"numeric", minute:"2-digit" })}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// =====================================================================
// Add Pod dialog (rendered inside PodLocationsSection via portal below)
// =====================================================================
function AddPodDialog({ open, onClose, onCreated }) {
  const [form, setForm] = useState({ slug: "", name: "", neighborhood: "", address: "", lat: "", lng: "", status: "opening_soon", hours: "24/7 self-serve", tagline: "", launch_date: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = { ...form, lat: form.lat ? parseFloat(form.lat) : null, lng: form.lng ? parseFloat(form.lng) : null };
      await api.post("/admin/pods", body);
      toast.success(`${form.name} created`);
      onCreated();
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Create failed");
    } finally { setBusy(false); }
  };
  if (!open) return null;
  const bind = (k) => ({ value: form[k], onChange: (e) => setForm({ ...form, [k]: e.target.value }) });
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg bg-white rounded-[28px] max-h-[90vh] overflow-y-auto" data-testid="add-pod-dialog">
        <DialogHeader>
          <DialogTitle className="text-brand-blue font-black uppercase tracking-tightest text-xl">Add New Pod</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <FormField label="Slug (URL-safe)" value={form.slug} onChange={(v) => setForm({ ...form, slug: v.toLowerCase().replace(/[^a-z0-9-]/g, "-") })} testId="pod-form-slug" />
          <FormField label="Name" {...bind("name")} testId="pod-form-name" />
          <FormField label="Neighborhood" {...bind("neighborhood")} testId="pod-form-neighborhood" />
          <FormField label="Address" {...bind("address")} testId="pod-form-address" />
          <div className="grid grid-cols-2 gap-3">
            <FormField label="Latitude"  {...bind("lat")} testId="pod-form-lat" />
            <FormField label="Longitude" {...bind("lng")} testId="pod-form-lng" />
          </div>
          <div>
            <label className="block text-[10px] font-black uppercase tracking-widest text-brand-blue mb-1">Status</label>
            <select {...bind("status")} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm font-bold" data-testid="pod-form-status">
              <option value="opening_soon">Opening Soon</option>
              <option value="active">Active (Live)</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          <FormField label="Hours" {...bind("hours")} testId="pod-form-hours" />
          <FormField label="Tagline" {...bind("tagline")} testId="pod-form-tagline" />
          <FormField label="Launch Date (YYYY-MM-DD)" {...bind("launch_date")} testId="pod-form-launch" />
          <button type="submit" disabled={busy} className="btn-primary w-full py-3 rounded-xl font-black uppercase text-[10px] tracking-widest disabled:opacity-50" data-testid="pod-form-submit">
            {busy ? <><i className="fas fa-spinner fa-spin mr-2" /> Creating</> : "Create Pod"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}


// =====================================================================
// Create Rental — admin-side booking creation (walk-in or send payment link)
// =====================================================================
function CreateRentalDialog({ onClose, onCreated }) {
  const [pods, setPods] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [lines, setLines] = useState({});  // {item_id: qty}
  const [form, setForm] = useState({
    customer_email: "",
    customer_name: "",
    customer_phone: "",
    start_date: new Date().toISOString().slice(0, 10),
    rental_days: 1,
    pod_slug: "park-hill",
    notes: "",
    late_checkout: false,
    mark_paid_walkin: false,
    send_payment_link: true,
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/pods"), api.get("/inventory")])
      .then(([p, inv]) => { setPods(p.data.items || []); setInventory(inv.data || []); })
      .catch(() => {});
  }, []);

  const bumpQty = (id, delta) => {
    setLines((prev) => {
      const next = { ...prev };
      next[id] = Math.max(0, (next[id] || 0) + delta);
      if (next[id] === 0) delete next[id];
      return next;
    });
  };

  const subtotal = Object.entries(lines).reduce((s, [id, qty]) => {
    const item = inventory.find((i) => i.id === id);
    return s + (item ? item.price_per_day * qty * form.rental_days : 0);
  }, 0);

  const submit = async (e) => {
    e.preventDefault();
    const items = Object.entries(lines).map(([item_id, quantity]) => ({ item_id, quantity, extras: [] }));
    if (items.length === 0) { toast.error("Add at least one item"); return; }
    if (!form.mark_paid_walkin && !form.send_payment_link) { toast.error("Pick a payment flow"); return; }

    setBusy(true);
    try {
      const { data } = await api.post("/admin/bookings", {
        items,
        rental_days: form.rental_days,
        start_date: new Date(form.start_date).toISOString(),
        pod_slug: form.pod_slug,
        customer_email: form.customer_email,
        customer_name: form.customer_name || null,
        customer_phone: form.customer_phone || null,
        notes: form.notes || null,
        late_checkout: form.late_checkout,
        mark_paid_walkin: form.mark_paid_walkin,
        send_payment_link: form.send_payment_link,
      });
      toast.success(`Booking ${data.booking.id.slice(0,8)} created` + (data.payment_link ? " — payment link ready" : ""));
      if (data.payment_link) {
        try { navigator.clipboard.writeText(data.payment_link); toast.info("Payment link copied to clipboard"); } catch { /* clipboard unavailable */ }
      }
      onCreated();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Create failed");
    } finally { setBusy(false); }
  };

  const bind = (k) => ({ value: form[k], onChange: (e) => setForm({ ...form, [k]: e.target.value }) });

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl bg-white rounded-[28px] max-h-[90vh] overflow-y-auto" data-testid="create-rental-dialog">
        <DialogHeader>
          <DialogTitle className="text-brand-blue font-black uppercase tracking-tightest text-2xl">Create Rental</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-5">

          {/* Customer */}
          <div>
            <p className="text-[10px] font-black uppercase tracking-widest text-brand-red mb-2">Customer</p>
            <div className="grid sm:grid-cols-3 gap-3">
              <FormField label="Email" {...bind("customer_email")} testId="cr-email" />
              <FormField label="Name (optional)" {...bind("customer_name")} testId="cr-name" />
              <FormField label="Phone (optional)" {...bind("customer_phone")} testId="cr-phone" />
            </div>
          </div>

          {/* Dates + Pod */}
          <div>
            <p className="text-[10px] font-black uppercase tracking-widest text-brand-red mb-2">Rental</p>
            <div className="grid sm:grid-cols-3 gap-3">
              <div>
                <label className="block text-[10px] font-black uppercase tracking-widest text-brand-blue mb-1">Start Date</label>
                <input type="date" {...bind("start_date")} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm font-bold" data-testid="cr-start" />
              </div>
              <div>
                <label className="block text-[10px] font-black uppercase tracking-widest text-brand-blue mb-1">Days</label>
                <input type="number" min={1} max={30} value={form.rental_days} onChange={(e) => setForm({ ...form, rental_days: parseInt(e.target.value || "1", 10) })} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm font-bold" data-testid="cr-days" />
              </div>
              <div>
                <label className="block text-[10px] font-black uppercase tracking-widest text-brand-blue mb-1">Pod</label>
                <select {...bind("pod_slug")} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm font-bold" data-testid="cr-pod">
                  {pods.map((p) => (
                    <option key={p.slug} value={p.slug}>{p.name} {p.status !== "active" && "(soon)"}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Items */}
          <div>
            <p className="text-[10px] font-black uppercase tracking-widest text-brand-red mb-2">Items</p>
            <div className="bg-slate-50 rounded-2xl p-3 space-y-1 max-h-64 overflow-y-auto">
              {inventory.map((item) => {
                const qty = lines[item.id] || 0;
                const stock = (item.stock_by_pod || {})[form.pod_slug] ?? 0;
                return (
                  <div key={item.id} className="flex items-center justify-between gap-3 bg-white p-2 rounded-lg">
                    <div className="flex-1 min-w-0">
                      <p className="font-black text-brand-blue text-xs uppercase tracking-tight truncate">{item.name}</p>
                      <p className="text-[10px] text-slate-400 font-bold">${item.price_per_day}/day · {stock} at pod</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button type="button" onClick={() => bumpQty(item.id, -1)} disabled={qty === 0} className="w-7 h-7 rounded-full bg-slate-100 font-black text-brand-blue disabled:opacity-30">-</button>
                      <span className="w-6 text-center font-black text-brand-blue text-sm" data-testid={`cr-qty-${item.id}`}>{qty}</span>
                      <button type="button" onClick={() => bumpQty(item.id, 1)} disabled={qty >= stock} className="w-7 h-7 rounded-full bg-brand-blue text-white font-black disabled:opacity-30" data-testid={`cr-plus-${item.id}`}>+</button>
                    </div>
                  </div>
                );
              })}
            </div>
            {Object.keys(lines).length > 0 && (
              <p className="mt-2 text-right font-black text-brand-blue text-lg" data-testid="cr-subtotal">Subtotal: ${subtotal.toFixed(2)}</p>
            )}
          </div>

          {/* Payment */}
          <div>
            <p className="text-[10px] font-black uppercase tracking-widest text-brand-red mb-2">Payment</p>
            <label className="flex items-center gap-2 py-1.5">
              <input type="checkbox" checked={form.mark_paid_walkin} onChange={(e) => setForm({ ...form, mark_paid_walkin: e.target.checked })} data-testid="cr-walkin" />
              <span className="text-sm font-bold text-brand-blue">Mark as paid (walk-in / cash / venmo)</span>
            </label>
            <label className="flex items-center gap-2 py-1.5">
              <input type="checkbox" checked={form.send_payment_link} onChange={(e) => setForm({ ...form, send_payment_link: e.target.checked })} data-testid="cr-paylink" />
              <span className="text-sm font-bold text-brand-blue">Send Stripe payment link (booking stays pending until paid)</span>
            </label>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-[10px] font-black uppercase tracking-widest text-brand-blue mb-1">Notes (optional)</label>
            <textarea rows={2} {...bind("notes")} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm font-bold" data-testid="cr-notes" />
          </div>

          <button type="submit" disabled={busy} className="btn-primary w-full py-3 rounded-xl font-black uppercase text-[10px] tracking-widest disabled:opacity-50" data-testid="cr-submit">
            {busy ? <><i className="fas fa-spinner fa-spin mr-2" /> Creating</> : "Create Booking"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

